// ============================================================================
// STAKEHOLDER ONBOARDING MODAL
// Multi-step wizard: Stakeholder → People → Locations → Review
// Replaces sequential ContactModal → PersonModal → LocationModal flow
// Date: March 30, 2026
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/contacts-stakeholders.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/contacts-stakeholders#adding-a-stakeholder)
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  X,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Mail,
  Briefcase,
  Upload,
  CreditCard,
  Shield,
  Award,
  Plus,
  Trash2,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Users,
  MapPinned,
  ClipboardCheck,
  AlertCircle,
  CheckCircle2,
  FileText,
  Edit,
  Send,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import GoogleMapsPicker from '@/components/maps/GoogleMapsPicker';
import { useTheme } from '@/contexts/ThemeContext';
import PhoneInputWithCountryCode from './PhoneInputWithCountryCode';
import {
  TextInput,
  SelectInput,
  TextareaInput,
  SectionHeader,
  AccordionCard,
} from './StakeholderFormComponents';
import EmployeeLookup from '@/components/shared/EmployeeLookup';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { useContactConfig } from '@/hooks/useContactConfig';
import {
  CONTACT_ACCOUNT_STATUS_OPTIONS,
  type ContactAccountStatus,
  isActiveContactAccountStatus,
} from '@/lib/contacts/accountStanding';
import FinanceReportViewer from '@/components/reports/FinanceReportViewer';
import { getFinanceReportById } from '@/lib/shared/financeReportDefinitions';
import type { ReportDefinition } from '@/types/reports/shared-reports';
import { GuidedTour } from '@/components/training/guided-tour';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// STATIC CONFIG
// ============================================================================

const STATIC_FILE_SERVER_BASE = 'https://staticfileserver.totalaccess.co.za';
const STATIC_FILE_API_KEY = process.env.NEXT_PUBLIC_STATIC_FILE_SERVER_API_KEY || 'a11abd7b-0d47-4fe2-a66f-f7c405cf5f80';

const getAuthToken = () => authService.getTokens()?.access_token || null;

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

async function uploadImage(file: File, folder: string): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const response = await fetch(`${STATIC_FILE_SERVER_BASE}/upload`, {
      method: 'POST',
      headers: { 'X-API-Key': STATIC_FILE_API_KEY },
      body: formData,
    });
    if (response.ok) {
      const data = await response.json();
      return data.directUrl || data.url || (data.publicUrl ? `${STATIC_FILE_SERVER_BASE}${data.publicUrl}` : null);
    }
    return null;
  } catch {
    return null;
  }
}

// ============================================================================
// TYPES
// ============================================================================

type StakeholderStep = 'stakeholder' | 'people' | 'locations' | 'review';
type StakeholderSectionKey = 'business' | 'financial' | 'procurement';
type PersonType = 'authorized_representative' | 'authorized_signatory' | 'contact_person' | 'resident';

interface StakeholderForm {
  contact_type: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  mobile: string;
  website: string;
  tax_number: string;
  registration_number: string;
  payment_terms: string;
  payment_terms_other: string;
  credit_limit: number | null;
  currency: string;
  account_status: ContactAccountStatus;
  is_active: boolean;
  notes: string;
  logo_url: string | null;
  photo_url: string | null;
  legal_name: string;
  legal_address: string;
  trading_name: string;
  industry: string;
  company_size: string;
  annual_revenue: string;
  tax_clearance_verified_at: string;
  procurement_review_date: string;
  procurement_review_notes: string;
  account_manager_name: string;
  account_manager_id: number | null;
  credit_assessment_notes: string;
  bank_name: string;
  bank_account_number: string;
  bank_branch_code: string;
  bank_account_type: string;
  bee_level: string;
  bee_expiry_date: string;
  insurance_provider: string;
  insurance_policy_number: string;
  insurance_expiry_date: string;
  license_number: string;
  license_expiry_date: string;
  trade_references: Array<{ company: string; contact: string; phone: string; email: string }>;
  certifications: Array<{ name: string; number: string; expiry: string }>;
  skills: string[];
  hourly_rate: number | null;
  daily_rate: number | null;
  lead_time_days: number | null;
  minimum_order_value: number | null;
  discount_percentage: number | null;
  preferred_payment_method: string;
}

interface PersonForm {
  _key: string; // local key for react
  first_name: string;
  last_name: string;
  person_type: PersonType;
  email: string;
  phone: string;
  mobile: string;
  direct_line: string;
  job_title: string;
  department: string;
  preferred_contact_method: 'email' | 'phone' | 'mobile' | 'whatsapp';
  is_primary: boolean;
  is_billing_contact: boolean;
  is_technical_contact: boolean;
  is_decision_maker: boolean;
  is_active: boolean;
  notes: string;
  photo_url: string | null;
  // Resident fields — only relevant when person_type === 'resident'
  // (visitor-management workflow enabled + customer stakeholder)
  resident_role: string;          // role_at_location on contact_people_locations
  resident_location_key: string;  // _key of a LocationForm in this onboarding
  accepts_gate_calls: boolean;
  has_portal_access: boolean;
}

interface LocationForm {
  _key: string;
  location_name: string;
  location_type: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  radius_meters: number;
  is_primary: boolean;
  is_billing: boolean;
  is_shipping: boolean;
  is_active: boolean;
  is_company_location: boolean;
  no_appointment_required: boolean;
  notes: string;
  billing_entity_name: string;
  billing_registration_number: string;
  billing_tax_number: string;
  billing_email: string;
  billing_phone: string;
  billing_attention: string;
  location_code: string;
  cost_centre: string;
}

const defaultStakeholder: StakeholderForm = {
  contact_type: 'customer',
  company_name: '',
  contact_person: '',
  email: '',
  phone: '',
  mobile: '',
  website: '',
  tax_number: '',
  registration_number: '',
  payment_terms: 'cod',
  payment_terms_other: '',
  credit_limit: null,
  currency: 'ZAR',
  account_status: 'draft',
  is_active: true,
  notes: '',
  logo_url: null,
  photo_url: null,
  legal_name: '',
  legal_address: '',
  trading_name: '',
  industry: '',
  company_size: '',
  annual_revenue: '',
  tax_clearance_verified_at: '',
  procurement_review_date: '',
  procurement_review_notes: '',
  account_manager_name: '',
  account_manager_id: null,
  credit_assessment_notes: '',
  bank_name: '',
  bank_account_number: '',
  bank_branch_code: '',
  bank_account_type: '',
  bee_level: '',
  bee_expiry_date: '',
  insurance_provider: '',
  insurance_policy_number: '',
  insurance_expiry_date: '',
  license_number: '',
  license_expiry_date: '',
  trade_references: [],
  certifications: [],
  skills: [],
  hourly_rate: null,
  daily_rate: null,
  lead_time_days: null,
  minimum_order_value: null,
  discount_percentage: null,
  preferred_payment_method: '',
};

function makePersonForm(isPrimary = false): PersonForm {
  return {
    _key: crypto.randomUUID(),
    first_name: '',
    last_name: '',
    person_type: 'contact_person',
    email: '',
    phone: '',
    mobile: '',
    direct_line: '',
    job_title: '',
    department: '',
    preferred_contact_method: 'email',
    is_primary: isPrimary,
    is_billing_contact: false,
    is_technical_contact: false,
    is_decision_maker: false,
    is_active: true,
    notes: '',
    photo_url: null,
    resident_role: '',
    resident_location_key: '',
    accepts_gate_calls: false,
    has_portal_access: false,
  };
}

function makeLocationForm(isPrimary = false): LocationForm {
  return {
    _key: crypto.randomUUID(),
    location_name: '',
    location_type: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'South Africa',
    latitude: null,
    longitude: null,
    radius_meters: 100,
    is_primary: isPrimary,
    is_billing: false,
    is_shipping: false,
    is_active: true,
    is_company_location: false,
    no_appointment_required: false,
    notes: '',
    billing_entity_name: '',
    billing_registration_number: '',
    billing_tax_number: '',
    billing_email: '',
    billing_phone: '',
    billing_attention: '',
    location_code: '',
    cost_centre: '',
  };
}

function formatGpsCoordinates(latitude: unknown, longitude: unknown): string | null {
  const lat = toFiniteNumber(latitude);
  const lng = toFiniteNumber(longitude);

  if (lat === null || lng === null) {
    return null;
  }

  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

// ============================================================================
// LOCAL STORAGE PERSISTENCE — saves in-progress onboarding so the user
// doesn't lose data if the modal is closed or the page is refreshed.
// Cleared on successful creation.
// ============================================================================

const DRAFT_STORAGE_KEY = 'ta_stakeholder_onboarding_draft';

interface OnboardingDraft {
  currentStep: StakeholderStep;
  stakeholder: StakeholderForm;
  people: PersonForm[];
  locations: LocationForm[];
  activePersonIdx: number;
  activeLocationIdx: number;
  savedAt: string; // ISO timestamp
}

function saveOnboardingDraft(draft: Omit<OnboardingDraft, 'savedAt'>): void {
  try {
    const payload: OnboardingDraft = { ...draft, savedAt: new Date().toISOString() };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be unavailable (private mode, quota) — silent fail
  }
}

function loadOnboardingDraft(): OnboardingDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardingDraft;
    if (!parsed || !parsed.stakeholder || !Array.isArray(parsed.people) || !Array.isArray(parsed.locations)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearOnboardingDraft(): void {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // silent
  }
}

// ============================================================================
// PROPS
// ============================================================================

interface StakeholderOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (contactId?: number) => void;
  /** Pre-fill contact type */
  defaultContactType?: string;
  /** Modal mode: create (default), edit, or view */
  mode?: 'create' | 'edit' | 'view';
  /** Contact ID to load for edit/view mode */
  editContactId?: number;
  /** Portal access mode - simplified UI for granting FinancialCustomerPortal access */
  portalAccessMode?: boolean;
  /** Person ID for portal access mode */
  portalAccessPersonId?: number;
  /** Module key the modal is hosted in (e.g. 'total-finance', 'total-operations').
   *  When 'total-finance', the Locations step becomes optional/skippable because
   *  finance stakeholders (incl. QBO/Sage/Xero imports) often have no GPS coordinates. */
  moduleKey?: string;
}

type ActiveFinanceReportView = {
  report: ReportDefinition;
  initialParameters: Record<string, any>;
  autoExecute: boolean;
};

const QUICK_ACTION_REPORT_CONFIG: Record<string, { reportId: string; getParameters: (contactId: number, dateFrom: string, dateTo: string) => Record<string, any>; autoExecute: boolean }> = {
  customer_statement: {
    reportId: 'finance_customer_statement',
    getParameters: (contactId, dateFrom, dateTo) => ({
      contact_id: contactId,
      date_range: {
        start: dateFrom,
        end: dateTo,
      },
    }),
    autoExecute: true,
  },
  aged_receivables: {
    reportId: 'finance_aged_receivables',
    getParameters: (_contactId, _dateFrom, dateTo) => ({
      as_of_date: dateTo,
    }),
    autoExecute: true,
  },
  debtor_reconciliation: {
    reportId: 'finance_debtor_reconciliation',
    getParameters: (_contactId, dateFrom, dateTo) => ({
      date_range: {
        start: dateFrom,
        end: dateTo,
      },
    }),
    autoExecute: true,
  },
  sales_by_customer: {
    reportId: 'finance_sales_by_customer',
    getParameters: (_contactId, dateFrom, dateTo) => ({
      date_range: {
        start: dateFrom,
        end: dateTo,
      },
    }),
    autoExecute: true,
  },
};

const PAYMENT_TERMS_DAY_MAP: Record<string, number> = {
  cod: 0,
  fob: 0,
  '7_days': 7,
  '14_days': 14,
  '30_days': 30,
  '60_days': 60,
  '90_days': 90,
};

const PERSON_TYPE_OPTIONS: Array<{ value: PersonType; label: string }> = [
  { value: 'authorized_representative', label: 'Authorized Representative' },
  { value: 'authorized_signatory', label: 'Authorized Signatory' },
  { value: 'contact_person', label: 'Regular Contact Person' },
  { value: 'resident', label: 'Resident' },
];

const RESIDENT_ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'resident', label: 'Resident' },
];

function getPersonTypeLabel(type: PersonType) {
  return PERSON_TYPE_OPTIONS.find((option) => option.value === type)?.label || 'Regular Contact Person';
}

function buildPersonNotes(person: PersonForm): string | null {
  const sections: string[] = [];

  if (person.person_type !== 'contact_person') {
    sections.push(`Role category: ${getPersonTypeLabel(person.person_type)}`);
  }

  if (person.notes.trim()) {
    sections.push(person.notes.trim());
  }

  return sections.length > 0 ? sections.join('\n\n') : null;
}

function buildProcurementInstructions(stakeholder: StakeholderForm): string | null {
  const items = [
    stakeholder.payment_terms === 'other' && stakeholder.payment_terms_other.trim()
      ? `Custom payment terms: ${stakeholder.payment_terms_other.trim()}`
      : null,
    stakeholder.tax_clearance_verified_at ? `Tax clearance verified at: ${stakeholder.tax_clearance_verified_at}` : null,
    stakeholder.procurement_review_date ? `Procurement review date: ${stakeholder.procurement_review_date}` : null,
    stakeholder.account_manager_name ? `Account manager / relationship owner: ${stakeholder.account_manager_name}` : null,
    stakeholder.credit_assessment_notes ? `Credit / guarantee notes: ${stakeholder.credit_assessment_notes}` : null,
    stakeholder.procurement_review_notes ? `Procurement review notes: ${stakeholder.procurement_review_notes}` : null,
  ].filter(Boolean) as string[];

  return items.length > 0 ? `Procurement details\n${items.map((item) => `- ${item}`).join('\n')}` : null;
}

function getPaymentTermsDays(paymentTerms: string, customTerms: string) {
  if (paymentTerms in PAYMENT_TERMS_DAY_MAP) {
    return PAYMENT_TERMS_DAY_MAP[paymentTerms];
  }

  const match = customTerms.match(/\d+/);
  return match ? parseInt(match[0], 10) : 30;
}

function handleAccountStatusChange(
  nextStatus: ContactAccountStatus,
  setStakeholder: React.Dispatch<React.SetStateAction<StakeholderForm>>,
) {
  setStakeholder((prev) => ({
    ...prev,
    account_status: nextStatus,
    is_active: nextStatus === 'draft' ? true : isActiveContactAccountStatus(nextStatus),
    payment_terms: nextStatus === 'cod' ? 'cod' : prev.payment_terms,
  }));
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function StakeholderOnboardingModal({
  isOpen,
  onClose,
  onSaved,
  defaultContactType,
  mode = 'create',
  editContactId,
  portalAccessMode = false,
  portalAccessPersonId,
  moduleKey,
}: StakeholderOnboardingModalProps) {
  const { clientBranding, primaryColor, currentTheme } = useTheme();
  const { config, getTypeLabel } = useContactConfig();
  const isDark = currentTheme?.mode === 'dark';
  const headerColor = clientBranding?.primary_color || primaryColor || '#3b82f6';

  // Step state
  const [currentStep, setCurrentStep] = useState<StakeholderStep>('stakeholder');
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Portal access state
  const [portalAccessData, setPortalAccessData] = useState<{ id: number; first_name: string; last_name: string; email?: string; phone?: string; job_title?: string; contact_id: number } | null>(null);
  const [portalInvitationSent, setPortalInvitationSent] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [customerPortalEnabled, setCustomerPortalEnabled] = useState(true);

  // Email/phone validation state for step 1 (stakeholder contact)
  const [emailValidation, setEmailValidation] = useState<{ isValid: boolean | null; error?: string; isKnownUser?: boolean }>({
    isValid: null,
    error: '',
    isKnownUser: false,
  });
  const [mobileValidation, setMobileValidation] = useState<{ isValid: boolean | null; error?: string; isKnownUser?: boolean }>({
    isValid: null,
    error: '',
    isKnownUser: false,
  });
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);

  // Email/phone validation state for step 2 (people)
  const [peopleValidation, setPeopleValidation] = useState<Record<number, {
    email: { isValid: boolean | null; error?: string; isKnownUser?: boolean };
    mobile: { isValid: boolean | null; error?: string; isKnownUser?: boolean };
  }>>({});
  const [peopleChecking, setPeopleChecking] = useState<Record<number, {
    email: boolean;
    mobile: boolean;
  }>>({});

  // Form data
  const [stakeholder, setStakeholder] = useState<StakeholderForm>({ ...defaultStakeholder });

  // Residents are a people category on customer stakeholders — only offered
  // when the visitor-management workflow is enabled (server-computed via
  // contacts/config.visitor_enabled).
  const canOnboardResidents = config.visitor_enabled === true && stakeholder.contact_type === 'customer';
  const personTypeOptions = useMemo(
    () => PERSON_TYPE_OPTIONS.filter((o) => o.value !== 'resident' || canOnboardResidents),
    [canOnboardResidents],
  );
  const [people, setPeople] = useState<PersonForm[]>([makePersonForm(true)]);
  const [locations, setLocations] = useState<LocationForm[]>([makeLocationForm(true)]);

  // Upload states
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Stakeholder accordions — allow multiple open in view mode
  const [openStakeholderSections, setOpenStakeholderSections] = useState<Set<StakeholderSectionKey>>(new Set());

  // Active accordion for people/locations
  const [activePersonIdx, setActivePersonIdx] = useState(0);
  const [activeLocationIdx, setActiveLocationIdx] = useState(0);
  // In finance context the map is optional — track which location (if any) has
  // its map picker expanded so the step stays lightweight for finance users.
  const [financeMapExpandedIdx, setFinanceMapExpandedIdx] = useState<number | null>(null);

  // Geocoding
  const [geocoding, setGeocoding] = useState(false);

  // Loading for edit/view
  const [loadingContact, setLoadingContact] = useState(false);
  const [generatingReport, setGeneratingReport] = useState<string | null>(null);
  const [activeFinanceReportView, setActiveFinanceReportView] = useState<ActiveFinanceReportView | null>(null);

  // Active employees for account manager selector (same API as DetailsSidebar)
  const [accountManagerOptions, setAccountManagerOptions] = useState<Array<{ id: number; label: string; subLabel?: string }>>([]);

  const isReadOnly = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Fetch active employees for the account manager dropdown
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const membersById = new Map<number, { id: number; label: string; subLabel?: string }>();
        let page = 1;
        let totalPages = 1;
        while (page <= totalPages) {
          const res = await fetch(`/api/v1/employees?page=${page}&limit=250&employment_status=active`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) break;
          const data = await res.json();
          const employees: Array<{
            id?: number | string | null;
            employee_number?: string | null;
            first_name?: string | null;
            last_name?: string | null;
            job_title?: string | null;
            user_id?: number | string | null;
            user?: { id?: number | string | null; first_name?: string | null; last_name?: string | null; email?: string | null } | null;
          }> = Array.isArray(data.employees) ? data.employees : Array.isArray(data.data?.employees) ? data.data.employees : [];
          for (const emp of employees) {
            const linkedUserId = Number(emp.user?.id ?? emp.user_id ?? 0);
            const memberId = Number.isFinite(linkedUserId) && linkedUserId > 0 ? linkedUserId : Number(emp.id ?? 0);
            if (!Number.isFinite(memberId) || memberId <= 0) continue;
            const rawFirst = String(emp.user?.first_name || emp.first_name || '').trim();
            const rawLast = String(emp.user?.last_name || emp.last_name || '').trim();
            const hasName = rawFirst.length > 0 || rawLast.length > 0;
            const firstName = hasName ? rawFirst : 'Employee';
            const lastName = hasName ? rawLast : String(emp.employee_number || emp.id || '');
            const label = `${firstName} ${lastName}`.trim();
            const jobTitle = String(emp.job_title || '').trim();
            const empNum = String(emp.employee_number || '').trim();
            const subLabel = [jobTitle, empNum].filter(Boolean).join(' • ') || undefined;
            membersById.set(memberId, { id: memberId, label, subLabel });
          }
          const nextTotal = Number(data.pagination?.pages ?? data.data?.pagination?.pages ?? 1);
          totalPages = Number.isFinite(nextTotal) && nextTotal > 0 ? nextTotal : 1;
          page += 1;
        }
        if (!cancelled) {
          setAccountManagerOptions(Array.from(membersById.values()).sort((a, b) => a.label.localeCompare(b.label)));
        }
      } catch {
        // silent — dropdown will just be empty
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Track whether we restored a draft on this open cycle so the auto-save
  // effect doesn't immediately overwrite the draft with default state.
  const [draftRestored, setDraftRestored] = useState(false);

  // ---- Reset on open (with draft restoration) ----
  useEffect(() => {
    if (isOpen && isCreateMode) {
      // Check for a previously saved in-progress onboarding draft
      const draft = loadOnboardingDraft();
      if (draft) {
        // Ask the user whether to resume the draft or start fresh
        notify
          .confirm({
            title: 'Resume onboarding?',
            message: 'You have an in-progress onboarding draft. Would you like to resume where you left off, or start a new one?',
            confirmText: 'Resume Draft',
            cancelText: 'Start Fresh',
            variant: 'info',
          })
          .then((resume) => {
            if (resume) {
              setStakeholder(draft.stakeholder);
              // Merge over makePersonForm()/makeLocationForm() so drafts saved
              // before new fields were added still have every key defined.
              setPeople(draft.people.length > 0 ? draft.people.map((p) => ({ ...makePersonForm(), ...p })) : [makePersonForm(true)]);
              setLocations(draft.locations.length > 0 ? draft.locations.map((l) => ({ ...makeLocationForm(), ...l })) : [makeLocationForm(true)]);
              setCurrentStep(draft.currentStep || 'stakeholder');
              setActivePersonIdx(draft.activePersonIdx ?? 0);
              setActiveLocationIdx(draft.activeLocationIdx ?? 0);
              setDraftRestored(true);
            } else {
              // Discard the draft and start fresh
              clearOnboardingDraft();
              setStakeholder({
                ...defaultStakeholder,
                contact_type: defaultContactType || config.default_contact_type || 'customer',
                account_status: 'draft',
                is_active: true,
              });
              setPeople([makePersonForm(true)]);
              setLocations([makeLocationForm(true)]);
              setCurrentStep('stakeholder');
              setDraftRestored(false);
            }
          });
      } else {
        // No draft — fresh start
        setStakeholder({
          ...defaultStakeholder,
          contact_type: defaultContactType || config.default_contact_type || 'customer',
          account_status: 'draft',
          is_active: true,
        });
        setPeople([makePersonForm(true)]);
        setLocations([makeLocationForm(true)]);
        setCurrentStep('stakeholder');
        setDraftRestored(false);
      }
      setValidationErrors({});
      setOpenStakeholderSections(new Set());
      setFinanceMapExpandedIdx(null);
      setActiveFinanceReportView(null);
      // Reset validation state
      setEmailValidation({ isValid: null, error: '', isKnownUser: false });
      setMobileValidation({ isValid: null, error: '', isKnownUser: false });
      setIsCheckingEmail(false);
      setIsCheckingMobile(false);
      setPeopleValidation({});
      setPeopleChecking({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isCreateMode]);

  // ---- Auto-save draft to localStorage on step / form data changes ----
  useEffect(() => {
    if (!isOpen || !isCreateMode) return;
    // Skip the initial render cycle when we just restored a draft — there's
    // nothing new to save and we don't want to overwrite the timestamp.
    if (draftRestored) {
      setDraftRestored(false);
      return;
    }
    saveOnboardingDraft({
      currentStep,
      stakeholder,
      people,
      locations,
      activePersonIdx,
      activeLocationIdx,
    });
  }, [isOpen, isCreateMode, draftRestored, currentStep, stakeholder, people, locations, activePersonIdx, activeLocationIdx]);

  useEffect(() => {
    if (!isOpen) {
      setActiveFinanceReportView(null);
      setGeneratingReport(null);
    }
  }, [isOpen]);

  // ---- Load person data for portal access mode ----
  const loadPortalAccessPersonData = useCallback(async () => {
    if (!portalAccessPersonId) return;
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/contacts/people/${portalAccessPersonId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success && result.data) {
        setPortalAccessData(result.data);
      }
    } catch (error) {
      console.error('Error loading person data:', error);
    }
  }, [portalAccessPersonId]);

  useEffect(() => {
    if (isOpen && portalAccessMode && portalAccessPersonId) {
      loadPortalAccessPersonData();
    }
  }, [isOpen, portalAccessMode, portalAccessPersonId, loadPortalAccessPersonData]);

  // ---- Send portal invitation ----
  const handleSendPortalInvitation = async () => {
    if (!customerPortalEnabled) {
      setPortalError('Customer portal is currently disabled. Please enable it in admin-hub settings.');
      return;
    }
    
    if (!portalAccessData || (!portalAccessData.email && !portalAccessData.phone)) {
      setPortalError('Person must have an email or phone number to receive an invitation.');
      return;
    }

    setPortalLoading(true);
    setPortalError(null);

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/portal/invite', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: portalAccessData.email,
          onboarding_type: 'financial_customer_portal',
          contact_id: portalAccessData.contact_id,
          contact_person_id: portalAccessData.id,
          portal_type: 'financial_customer_portal',
        }),
      });

      const result = await response.json();
      if (result.success) {
        setPortalInvitationSent(true);
        notify.success('Portal invitation sent successfully');
      } else {
        setPortalError(result.error || 'Failed to send invitation');
      }
    } catch (error: unknown) {
      setPortalError(error instanceof Error ? error.message : 'Failed to send invitation');
    } finally {
      setPortalLoading(false);
    }
  };

  // ---- Email validation effect (format + known user detection, no blocking) ----
  useEffect(() => {
    if (!stakeholder.email) {
      setEmailValidation({ isValid: null, error: '', isKnownUser: false });
      setIsCheckingEmail(false);
      return;
    }

    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(stakeholder.email)) {
      setEmailValidation({ isValid: false, error: 'Please enter a valid email address', isKnownUser: false });
      setIsCheckingEmail(false);
      return;
    }

    // Check for known user (display only, don't block)
    setIsCheckingEmail(true);
    const timeoutId = setTimeout(async () => {
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch('/api/v1/auth/check-email', {
          method: 'POST',
          headers,
          body: JSON.stringify({ email: stakeholder.email })
        });
        const data = await response.json();

        if (data.existingUser) {
          setEmailValidation({
            isValid: true,
            error: 'Known user - this email belongs to an existing account',
            isKnownUser: true,
          });
        } else {
          setEmailValidation({ isValid: true, error: '', isKnownUser: false });
        }
      } catch {
        // On error, allow but don't show known user
        setEmailValidation({ isValid: true, error: '', isKnownUser: false });
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [stakeholder.email]);

  // ---- People email validation effects ----
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    people.forEach((person, idx) => {
      if (!person.email) {
        setPeopleValidation(prev => ({
          ...prev,
          [idx]: { ...prev[idx], email: { isValid: null, error: '', isKnownUser: false } }
        }));
        setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], email: false } }));
        return;
      }

      // Check email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(person.email)) {
        setPeopleValidation(prev => ({
          ...prev,
          [idx]: { ...prev[idx], email: { isValid: false, error: 'Please enter a valid email address', isKnownUser: false } }
        }));
        setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], email: false } }));
        return;
      }

      // Check for known user (display only, don't block)
      setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], email: true } }));
      const timeoutId = setTimeout(async () => {
        try {
          const token = getAuthToken();
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const response = await fetch('/api/v1/auth/check-email', {
            method: 'POST',
            headers,
            body: JSON.stringify({ email: person.email })
          });
          const data = await response.json();

          if (data.existingUser) {
            setPeopleValidation(prev => ({
              ...prev,
              [idx]: { ...prev[idx], email: { isValid: true, error: 'Known user - this email belongs to an existing account', isKnownUser: true } }
            }));
          } else {
            setPeopleValidation(prev => ({
              ...prev,
              [idx]: { ...prev[idx], email: { isValid: true, error: '', isKnownUser: false } }
            }));
          }
        } catch {
          setPeopleValidation(prev => ({
            ...prev,
            [idx]: { ...prev[idx], email: { isValid: true, error: '', isKnownUser: false } }
          }));
        } finally {
          setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], email: false } }));
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    });
  }, [people]);

  // ---- Mobile validation effect (format + known user detection, no blocking) ----
  useEffect(() => {
    if (!stakeholder.mobile) {
      setMobileValidation({ isValid: null, error: '', isKnownUser: false });
      setIsCheckingMobile(false);
      return;
    }

    // Check if mobile is just a country code
    const justCountryCodePattern = /^\+\d{1,3}$/;
    if (justCountryCodePattern.test(stakeholder.mobile)) {
      setMobileValidation({ isValid: null, error: '', isKnownUser: false });
      setIsCheckingMobile(false);
      return;
    }

    // Check format (minimum 10 digits total including country code)
    const allDigits = stakeholder.mobile.replace(/\D/g, '');
    if (allDigits.length < 10) {
      setMobileValidation({ isValid: false, error: 'Phone number not complete', isKnownUser: false });
      setIsCheckingMobile(false);
      return;
    }

    // Check for known user (display only, don't block)
    setIsCheckingMobile(true);
    const timeoutId = setTimeout(async () => {
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch('/api/v1/auth/validate/phone', {
          method: 'POST',
          headers,
          body: JSON.stringify({ phone: stakeholder.mobile, email: stakeholder.email })
        });
        const data = await response.json();

        // If not unique, it's a known user
        let isUnique = false;
        if (data.isUnique !== undefined) {
          isUnique = data.isUnique;
        } else if (data.data?.isUnique !== undefined) {
          isUnique = data.data.isUnique;
        } else if (data.available !== undefined) {
          isUnique = data.available;
        } else if (data.data?.available !== undefined) {
          isUnique = data.data.available;
        }

        if (!isUnique) {
          setMobileValidation({
            isValid: true,
            error: 'Known user - this phone belongs to an existing account',
            isKnownUser: true,
          });
        } else {
          setMobileValidation({ isValid: true, error: '', isKnownUser: false });
        }
      } catch {
        // On error, allow but don't show known user
        setMobileValidation({ isValid: true, error: '', isKnownUser: false });
      } finally {
        setIsCheckingMobile(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [stakeholder.mobile, stakeholder.email]);

  // ---- People mobile validation effects ----
  useEffect(() => {
    people.forEach((person, idx) => {
      if (!person.mobile) {
        setPeopleValidation(prev => ({
          ...prev,
          [idx]: { ...prev[idx], mobile: { isValid: null, error: '', isKnownUser: false } }
        }));
        setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], mobile: false } }));
        return;
      }

      // Check if mobile is just a country code
      const justCountryCodePattern = /^\+\d{1,3}$/;
      if (justCountryCodePattern.test(person.mobile)) {
        setPeopleValidation(prev => ({
          ...prev,
          [idx]: { ...prev[idx], mobile: { isValid: null, error: '', isKnownUser: false } }
        }));
        setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], mobile: false } }));
        return;
      }

      // Check format (minimum 10 digits total including country code)
      const allDigits = person.mobile.replace(/\D/g, '');
      if (allDigits.length < 10) {
        setPeopleValidation(prev => ({
          ...prev,
          [idx]: { ...prev[idx], mobile: { isValid: false, error: 'Phone number not complete', isKnownUser: false } }
        }));
        setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], mobile: false } }));
        return;
      }

      // Check for known user (display only, don't block)
      setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], mobile: true } }));
      const timeoutId = setTimeout(async () => {
        try {
          const token = getAuthToken();
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const response = await fetch('/api/v1/auth/validate/phone', {
            method: 'POST',
            headers,
            body: JSON.stringify({ phone: person.mobile, email: person.email })
          });
          const data = await response.json();

          // If not unique, it's a known user
          let isUnique = false;
          if (data.isUnique !== undefined) {
            isUnique = data.isUnique;
          } else if (data.data?.isUnique !== undefined) {
            isUnique = data.data.isUnique;
          } else if (data.available !== undefined) {
            isUnique = data.available;
          } else if (data.data?.available !== undefined) {
            isUnique = data.data.available;
          }

          if (!isUnique) {
            setPeopleValidation(prev => ({
              ...prev,
              [idx]: { ...prev[idx], mobile: { isValid: true, error: 'Known user - this phone belongs to an existing account', isKnownUser: true } }
            }));
          } else {
            setPeopleValidation(prev => ({
              ...prev,
              [idx]: { ...prev[idx], mobile: { isValid: true, error: '', isKnownUser: false } }
            }));
          }
        } catch {
          setPeopleValidation(prev => ({
            ...prev,
            [idx]: { ...prev[idx], mobile: { isValid: true, error: '', isKnownUser: false } }
          }));
        } finally {
          setPeopleChecking(prev => ({ ...prev, [idx]: { ...prev[idx], mobile: false } }));
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    });
  }, [people]);

  // ---- Load existing contact for edit/view ----
  useEffect(() => {
    if (!isOpen || isCreateMode || !editContactId) return;

    const loadContact = async () => {
      setLoadingContact(true);
      try {
        const token = getAuthToken();
        const headers = { Authorization: `Bearer ${token}` };

        // Load contact details
        const contactRes = await fetch(`/api/v1/contacts/${editContactId}`, { headers });
        if (!contactRes.ok) {
          notify.error('Failed to load contact details');
          setLoadingContact(false);
          return;
        }
        const contactJson = await contactRes.json();
        const c = contactJson.data || contactJson;

        setStakeholder({
          contact_type: c.contact_type || 'customer',
          company_name: c.company_name || '',
          contact_person: c.contact_person || '',
          email: c.email || '',
          phone: c.phone || '',
          mobile: c.mobile || '',
          website: c.website || '',
          tax_number: c.tax_number || '',
          registration_number: c.registration_number || '',
          payment_terms: c.payment_terms || 'cod',
          payment_terms_other: c.payment_terms_other || '',
          credit_limit: c.credit_limit,
          currency: c.currency || 'ZAR',
          account_status: c.account_status || 'active',
          is_active: c.is_active !== false,
          notes: c.notes || '',
          logo_url: c.logo_url || null,
          photo_url: c.photo_url || null,
          legal_name: c.legal_name || '',
          legal_address: c.legal_address || '',
          trading_name: c.trading_name || '',
          industry: c.industry || '',
          company_size: c.company_size || '',
          annual_revenue: c.annual_revenue || '',
          tax_clearance_verified_at: c.tax_clearance_verified_at || '',
          procurement_review_date: c.procurement_review_date || '',
          procurement_review_notes: c.procurement_review_notes || '',
          account_manager_name: c.account_manager_name || '',
          account_manager_id: c.account_manager_id != null ? Number(c.account_manager_id) : null,
          credit_assessment_notes: c.credit_assessment_notes || '',
          bank_name: c.bank_name || '',
          bank_account_number: c.bank_account_number || '',
          bank_branch_code: c.bank_branch_code || '',
          bank_account_type: c.bank_account_type || '',
          bee_level: c.bee_level || '',
          bee_expiry_date: c.bee_expiry_date || '',
          insurance_provider: c.insurance_provider || '',
          insurance_policy_number: c.insurance_policy_number || '',
          insurance_expiry_date: c.insurance_expiry_date || '',
          license_number: c.license_number || '',
          license_expiry_date: c.license_expiry_date || '',
          trade_references: c.trade_references || [],
          certifications: c.certifications || [],
          skills: c.skills || [],
          hourly_rate: c.hourly_rate,
          daily_rate: c.daily_rate,
          lead_time_days: c.lead_time_days,
          minimum_order_value: c.minimum_order_value,
          discount_percentage: c.discount_percentage,
          preferred_payment_method: c.preferred_payment_method || '',
        });

        // Load people
        const { fetchContactPeople } = await import('@/lib/contacts/fetchContactPeople');
        const pData = await fetchContactPeople({ contactId: editContactId, token: getAuthToken() });
        if (pData.length > 0) {
            setPeople(pData.map((p: any, idx: number) => ({
              ...makePersonForm(),
              _key: `loaded-${p.id}`,
              first_name: p.first_name || '',
              last_name: p.last_name || '',
              person_type: p.person_type || 'contact_person',
              email: p.email || '',
              phone: p.phone || '',
              mobile: p.mobile || '',
              direct_line: p.direct_line || '',
              job_title: p.job_title || '',
              department: p.department || '',
              preferred_contact_method: p.preferred_contact_method || 'email',
              is_primary: p.is_primary || idx === 0,
              is_billing_contact: p.is_billing_contact || false,
              is_technical_contact: p.is_technical_contact || false,
              is_decision_maker: p.is_decision_maker || false,
              is_active: p.is_active !== false,
              notes: p.notes || '',
              photo_url: p.photo_url || null,
              accepts_gate_calls: p.accepts_gate_calls || false,
              has_portal_access: p.has_portal_access || false,
              // Loaded locations are keyed `loaded-${location_id}` — map the
              // person's default stand back to its form key.
              resident_location_key: p.default_location_id ? `loaded-${p.default_location_id}` : '',
            })));
          } else {
            setPeople([makePersonForm(true)]);
          }

        // Load locations
        const locRes = await fetch(`/api/v1/contacts/${editContactId}/locations/`, { headers });
        if (locRes.ok) {
          const locJson = await locRes.json();
          const lData = locJson.data || [];
          if (lData.length > 0) {
            setLocations(lData.map((l: any, idx: number) => ({
              _key: `loaded-${l.id}`,
              location_name: l.location_name || '',
              location_type: l.location_type || 'office',
              address_line1: l.address_line1 || '',
              address_line2: l.address_line2 || '',
              city: l.city || '',
              state: l.state || '',
              postal_code: l.postal_code || '',
              country: l.country || 'South Africa',
              latitude: toFiniteNumber(l.latitude),
              longitude: toFiniteNumber(l.longitude),
              radius_meters: toFiniteNumber(l.radius_meters) ?? 100,
              is_primary: l.is_primary || idx === 0,
              is_billing: l.is_billing || false,
              is_shipping: l.is_shipping || false,
              is_active: l.is_active !== false,
              is_company_location: l.is_company_location || false,
              no_appointment_required: l.no_appointment_required || false,
              notes: l.notes || '',
              billing_entity_name: l.billing_entity_name || '',
              billing_registration_number: l.billing_registration_number || '',
              billing_tax_number: l.billing_tax_number || '',
              billing_email: l.billing_email || '',
              billing_phone: l.billing_phone || '',
              billing_attention: l.billing_attention || '',
              location_code: l.location_code || '',
              cost_centre: l.cost_centre || '',
            })));
          } else {
            setLocations([makeLocationForm(true)]);
          }
        }

        setCurrentStep('stakeholder');
        setValidationErrors({});
        // In view mode, auto-expand all accordion sections so data is visible
        setOpenStakeholderSections(isReadOnly ? new Set<StakeholderSectionKey>(['business', 'financial', 'procurement']) : new Set());
        setActivePersonIdx(0);
        setActiveLocationIdx(0);
      } catch (error) {
        console.error('Error loading contact for edit/view:', error);
        notify.error('Failed to load contact details');
      } finally {
        setLoadingContact(false);
      }
    };

    void loadContact();
  }, [isOpen, isCreateMode, editContactId, isReadOnly]);

  // ---- Enabled contact types ----
  const enabledContactTypes = useMemo(() => {
    const types: Array<{ value: string; label: string }> = [];
    if (config.company_enabled) types.push({ value: 'company', label: config.company_label || 'My Company' });
    if (config.customer_enabled) types.push({ value: 'customer', label: config.customer_label || 'Customer' });
    if (config.supplier_enabled) types.push({ value: 'supplier', label: config.supplier_label || 'Supplier' });
    if (config.partner_enabled) types.push({ value: 'partner', label: config.partner_label || 'Partner' });
    if (config.contractor_enabled) types.push({ value: 'contractor', label: config.contractor_label || 'Contractor' });
    if (config.other_enabled) types.push({ value: 'other', label: config.other_label || 'Other' });
    return types;
  }, [config]);

  const contactType = stakeholder.contact_type || 'customer';
  const showContractorFields = contactType === 'contractor';
  const showFinancialFields = contactType === 'customer' || contactType === 'supplier';
  const allowFreeTextLocationAddress = Boolean(config.location_allow_free_text_address);
  // Finance context: stakeholders onboarded via total-finance (incl. QBO/Sage/Xero
  // imports) typically have no GPS coordinates. The Locations step is optional and
  // coordinates are never required here — they can be captured later from
  // ServiceJobModal / AppointmentModal when an operations job is first scheduled.
  const isFinanceContext = moduleKey === 'total-finance';
  // Coordinates are required only when NOT in finance context and the client has
  // not enabled free-text addresses (lookup-only mode forces a map pin).
  const coordinatesRequired = !isFinanceContext && !allowFreeTextLocationAddress;

  // Location types from config
  const locationTypes = useMemo(() => {
    const lt = config.location_types?.length ? config.location_types : ['office', 'warehouse', 'billing', 'shipping', 'service_location', 'job_site'];
    return lt.map((t) => ({ value: t, label: t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }));
  }, [config.location_types]);

  // ---- Steps definition ----
  const steps: Array<{ key: StakeholderStep; label: string; icon: React.ElementType; description: string }> = useMemo(
    () => [
      { key: 'stakeholder', label: config.contacts_singular_label || 'Stakeholder', icon: Building2, description: 'Company & business details' },
      { key: 'people', label: config.contact_people_label || 'People', icon: Users, description: 'Contact people at this company' },
      { key: 'locations', label: config.locations_label || 'Locations', icon: MapPinned, description: 'Sites, offices & addresses' },
      { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Review & create' },
    ],
    [config]
  );

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStep === 'review';

  // ---- Validation ----
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateStep = useCallback(
    (step: StakeholderStep): { ok: true } | { ok: false; firstError: string } => {
      const errors: Record<string, string> = {};

      if (step === 'stakeholder') {
        if (!stakeholder.company_name) {
          errors.company_name = 'Company name is required';
        }
        // Check email requirement and format (for business entity)
        if (config.contact_email_required && !stakeholder.email) {
          errors.email = 'Email is required';
        } else if (stakeholder.email && emailValidation.isValid === false) {
          errors.email = emailValidation.error || 'Invalid email format';
        }
        // Check mobile requirement and format (for business entity)
        if (config.contact_mobile_required && !stakeholder.mobile) {
          errors.mobile = 'Mobile is required';
        } else if (stakeholder.mobile && mobileValidation.isValid === false) {
          errors.mobile = mobileValidation.error || 'Invalid phone format';
        }
        if (config.contact_require_credit_limit && (stakeholder.credit_limit == null || stakeholder.credit_limit <= 0)) {
          errors.credit_limit = 'Credit limit is required';
        }
      }

      if (step === 'people') {
        people.forEach((p, i) => {
          if (!p.first_name) errors[`person_${i}_first_name`] = `Person ${i + 1}: First name required`;
          if (!p.last_name) errors[`person_${i}_last_name`] = `Person ${i + 1}: Last name required`;
          // Check email requirement and format
          if (config.person_email_required && !p.email) {
            errors[`person_${i}_email`] = `Person ${i + 1}: Email required`;
          } else if (p.email && peopleValidation[i]?.email.isValid === false) {
            errors[`person_${i}_email`] = `Person ${i + 1}: ${peopleValidation[i]?.email.error || 'Invalid email format'}`;
          }
          // Check mobile requirement and format
          if (config.person_mobile_required && !p.mobile) {
            errors[`person_${i}_mobile`] = `Person ${i + 1}: Mobile required`;
          } else if (p.mobile && peopleValidation[i]?.mobile.isValid === false) {
            errors[`person_${i}_mobile`] = `Person ${i + 1}: ${peopleValidation[i]?.mobile.error || 'Invalid phone format'}`;
          }
          if (config.person_phone_required && !p.phone) errors[`person_${i}_phone`] = `Person ${i + 1}: Phone required`;
        });
        if (config.contact_require_contact_person && !people.some((p) => p.first_name && p.last_name)) {
          errors.people_required = 'At least one contact person with first and last name is required';
        }
      }

      if (step === 'locations') {
        // In finance context the Locations step is optional: zero locations is
        // valid, and any locations the user does add still require name + type
        // but never require GPS coordinates.
        locations.forEach((l, i) => {
          if (!l.location_name) errors[`loc_${i}_name`] = `Location ${i + 1}: Name required`;
          if (!l.location_type) errors[`loc_${i}_type`] = `Location ${i + 1}: Type required`;
          if (coordinatesRequired) {
            if (l.latitude == null || l.longitude == null) {
              errors[`loc_${i}_gps`] = `Location ${i + 1}: Select the address from the map to set GPS coordinates`;
            }
            if (!l.address_line1) {
              errors[`loc_${i}_address`] = `Location ${i + 1}: Address must be populated from the map lookup`;
            }
          }
        });
      }

      setValidationErrors(errors);
      const msgs = Object.values(errors);
      if (msgs.length === 0) return { ok: true };
      return { ok: false, firstError: msgs[0] };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stakeholder, people, locations, config, allowFreeTextLocationAddress, coordinatesRequired, emailValidation, mobileValidation, peopleValidation]
  );

  // ---- Navigation ----
  const handleNext = () => {
    const result = validateStep(currentStep);
    if (!result.ok) {
      notify.error(result.firstError);
      return;
    }
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].key);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].key);
    }
  };

  // Skip the Locations step entirely (finance context only). Clears any
  // partially-entered locations so we don't persist incomplete rows, then
  // jumps straight to the review step.
  const skipLocationsStep = () => {
    setLocations([]);
    setValidationErrors({});
    setActiveLocationIdx(0);
    setCurrentStep('review');
  };

  const goToStep = (step: StakeholderStep) => {
    const targetIdx = steps.findIndex((s) => s.key === step);
    // Unknown ids (e.g. a tour's page-level target_tab) and forward jumps are
    // rejected; findIndex returns -1 for unknowns which would otherwise pass.
    if (targetIdx >= 0 && targetIdx <= currentStepIndex) {
      setCurrentStep(step);
    }
  };

  // ---- Logo upload ----
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const url = await uploadImage(file, 'contact-logos');
    if (url) {
      setStakeholder((prev) => ({ ...prev, logo_url: url }));
      notify.success('Logo uploaded');
    } else {
      notify.error('Failed to upload logo');
    }
    setUploadingLogo(false);
  };

  // ---- People helpers ----
  const addPerson = () => {
    const p = makePersonForm(people.length === 0);
    setPeople((prev) => [...prev, p]);
    setActivePersonIdx(people.length);
  };

  const removePerson = (idx: number) => {
    if (people.length <= 1) {
      notify.error('At least one contact person is required');
      return;
    }
    const wasPrimary = people[idx].is_primary;
    setPeople((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (wasPrimary && next.length > 0) {
        next[0] = { ...next[0], is_primary: true };
      }
      return next;
    });
    setActivePersonIdx((prev) => Math.min(prev, people.length - 2));
  };

  const updatePerson = (idx: number, patch: Partial<PersonForm>) => {
    setPeople((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const setPrimaryPerson = (idx: number) => {
    setPeople((prev) => prev.map((p, i) => ({ ...p, is_primary: i === idx })));
  };

  const addTradeReference = () => {
    setStakeholder((prev) => ({
      ...prev,
      trade_references: [...prev.trade_references, { company: '', contact: '', phone: '', email: '' }],
    }));
  };

  const updateTradeReference = (idx: number, patch: Partial<StakeholderForm['trade_references'][number]>) => {
    setStakeholder((prev) => ({
      ...prev,
      trade_references: prev.trade_references.map((reference, referenceIdx) =>
        referenceIdx === idx ? { ...reference, ...patch } : reference
      ),
    }));
  };

  const removeTradeReference = (idx: number) => {
    setStakeholder((prev) => ({
      ...prev,
      trade_references: prev.trade_references.filter((_, referenceIdx) => referenceIdx !== idx),
    }));
  };

  const addCertification = () => {
    setStakeholder((prev) => ({
      ...prev,
      certifications: [...prev.certifications, { name: '', number: '', expiry: '' }],
    }));
  };

  const updateCertification = (idx: number, patch: Partial<StakeholderForm['certifications'][number]>) => {
    setStakeholder((prev) => ({
      ...prev,
      certifications: prev.certifications.map((certification, certificationIdx) =>
        certificationIdx === idx ? { ...certification, ...patch } : certification
      ),
    }));
  };

  const removeCertification = (idx: number) => {
    setStakeholder((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, certificationIdx) => certificationIdx !== idx),
    }));
  };

  // ---- Location helpers ----
  const addLocation = () => {
    const l = makeLocationForm(locations.length === 0);
    setLocations((prev) => [...prev, l]);
    setActiveLocationIdx(locations.length);
  };

  const removeLocation = (idx: number) => {
    if (locations.length <= 1) {
      notify.error('At least one location is recommended');
      return;
    }
    const wasPrimary = locations[idx].is_primary;
    setLocations((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (wasPrimary && next.length > 0) {
        next[0] = { ...next[0], is_primary: true };
      }
      return next;
    });
    setActiveLocationIdx((prev) => Math.min(prev, locations.length - 2));
  };

  const updateLocation = (idx: number, patch: Partial<LocationForm>) => {
    setLocations((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const setPrimaryLocation = (idx: number) => {
    setLocations((prev) => prev.map((l, i) => ({ ...l, is_primary: i === idx })));
  };

  // ---- Map location select ----
  const handleLocationSelect = (idx: number, data: { lat: string; lng: string; address?: string }) => {
    const lat = parseFloat(data.lat);
    const lng = parseFloat(data.lng);
    let parsedAddress = { street: '', city: '', state: '', postal_code: '', country: '' };

    if (data.address) {
      const parts = data.address.split(',').map((p) => p.trim());
      if (parts.length >= 5) {
        parsedAddress = { street: parts[0], city: parts[2], state: '', postal_code: parts[3], country: parts[4] || '' };
      } else if (parts.length >= 4) {
        parsedAddress = { street: parts[0], city: parts[1], state: '', postal_code: parts[2], country: parts[3] || '' };
      } else if (parts.length >= 2) {
        parsedAddress = { street: parts[0], city: parts[1], state: '', postal_code: '', country: '' };
      }
    }

    updateLocation(idx, {
      latitude: lat,
      longitude: lng,
      address_line1: parsedAddress.street || data.address || locations[idx].address_line1,
      city: parsedAddress.city || locations[idx].city,
      state: parsedAddress.state || locations[idx].state,
      postal_code: parsedAddress.postal_code || locations[idx].postal_code,
      country: parsedAddress.country || locations[idx].country,
    });
  };

  // Geocode for a specific location
  const geocodeLocation = async (idx: number) => {
    const loc = locations[idx];
    const address = [loc.address_line1, loc.city, loc.state, loc.postal_code, loc.country].filter(Boolean).join(', ');
    if (!address || address.length < 5) {
      notify.error('Please enter an address to geocode');
      return;
    }
    setGeocoding(true);
    try {
      const response = await fetch(`/api/v1/geocode?address=${encodeURIComponent(address)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.lat && data.lng) {
          updateLocation(idx, { latitude: data.lat, longitude: data.lng });
          notify.success('Coordinates found');
        } else {
          notify.error('Could not find coordinates — please pin on map');
        }
      } else {
        notify.error('Geocode failed — please pin on map');
      }
    } catch {
      notify.error('Geocode failed — please pin on map');
    } finally {
      setGeocoding(false);
    }
  };

  // ---- SAVE ALL ----
  const handleSubmit = async () => {
    // Validate all steps
    for (const step of steps) {
      if (step.key === 'review') continue;
      const r = validateStep(step.key);
      if (!r.ok) {
        setCurrentStep(step.key);
        notify.error(r.firstError);
        return;
      }
    }

    // Ask user whether to keep the contact as draft or make it active
    let makeActive = false;
    const confirmed = await notify.confirm({
      title: 'Account Status',
      message: `Should this ${config.contacts_singular_label || 'stakeholder'} be made active now, or kept as draft? Draft contacts are not yet active and cannot transact until activated.`,
      confirmText: 'Make Active',
      cancelText: 'Cancel',
      variant: 'success',
      secondaryConfirmText: 'Keep as Draft',
      secondaryVariant: 'warning',
      onConfirm: async () => { makeActive = true; },
      onSecondaryConfirm: async () => { makeActive = false; },
    });

    if (!confirmed) return;

    setSaving(true);
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const specialInstructions = buildProcurementInstructions(stakeholder);
      const stakeholderPayload = {
        contact_type: stakeholder.contact_type,
        company_name: stakeholder.company_name || undefined,
        contact_person: stakeholder.contact_person || undefined,
        email: stakeholder.email || undefined,
        phone: stakeholder.phone || undefined,
        mobile: stakeholder.mobile || undefined,
        website: stakeholder.website || undefined,
        tax_number: stakeholder.tax_number || undefined,
        registration_number: stakeholder.registration_number || undefined,
        payment_terms_days: getPaymentTermsDays(stakeholder.payment_terms, stakeholder.payment_terms_other),
        credit_limit: stakeholder.credit_limit,
        currency: stakeholder.currency || 'ZAR',
        notes: stakeholder.notes || undefined,
        special_instructions: specialInstructions || undefined,
        is_active: true,
        account_status: makeActive ? 'active' : 'draft',
        logo_url: stakeholder.logo_url,
        photo_url: stakeholder.photo_url,
        legal_name: stakeholder.legal_name || undefined,
        legal_address: stakeholder.legal_address || undefined,
        trading_name: stakeholder.trading_name || undefined,
        industry: stakeholder.industry || undefined,
        company_size: stakeholder.company_size || undefined,
        annual_revenue: stakeholder.annual_revenue || undefined,
        bank_name: stakeholder.bank_name || undefined,
        bank_account_number: stakeholder.bank_account_number || undefined,
        bank_branch_code: stakeholder.bank_branch_code || undefined,
        bank_account_type: stakeholder.bank_account_type || undefined,
        bee_level: stakeholder.bee_level || undefined,
        bee_expiry_date: stakeholder.bee_expiry_date || undefined,
        insurance_provider: stakeholder.insurance_provider || undefined,
        insurance_policy_number: stakeholder.insurance_policy_number || undefined,
        insurance_expiry_date: stakeholder.insurance_expiry_date || undefined,
        license_number: stakeholder.license_number || undefined,
        license_expiry_date: stakeholder.license_expiry_date || undefined,
        trade_references: stakeholder.trade_references.filter((reference) =>
          [reference.company, reference.contact, reference.phone, reference.email].some(Boolean)
        ),
        certifications: stakeholder.certifications.filter((certification) =>
          [certification.name, certification.number, certification.expiry].some(Boolean)
        ),
        skills: stakeholder.skills,
        hourly_rate: stakeholder.hourly_rate,
        daily_rate: stakeholder.daily_rate,
        lead_time_days: stakeholder.lead_time_days,
        minimum_order_value: stakeholder.minimum_order_value,
        discount_percentage: stakeholder.discount_percentage,
        preferred_payment_method: stakeholder.preferred_payment_method || undefined,
        account_manager_id: stakeholder.account_manager_id ?? undefined,
      };

      // 1. Create the stakeholder/contact
      const contactRes = await fetch('/api/v1/contacts', {
        method: 'POST',
        headers,
        body: JSON.stringify(stakeholderPayload),
      });

      if (!contactRes.ok) {
        const err = await contactRes.json();
        notify.error(err.error || 'Failed to create stakeholder');
        setSaving(false);
        return;
      }

      const contactData = await contactRes.json();
      const contactId = contactData.data?.id || contactData.id;

      if (!contactId) {
        notify.error('Failed to get stakeholder ID');
        setSaving(false);
        return;
      }

      // 2. Create locations first (sequential to respect map/geocode data) —
      //    residents' stand links and default_location_id resolve against the
      //    new location ids.
      const validLocations = locations.filter((l) => l.location_name);
      let createdLocationsCount = 0;
      const locationIdByKey = new Map<string, string>();

      for (const loc of validLocations) {
        try {
          const locRes = await fetch(`/api/v1/contacts/${contactId}/locations`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              contact_id: contactId,
              name: loc.location_name,
              location_name: loc.location_name,
              location_type: loc.location_type,
              category: loc.location_type,
              address: [loc.address_line1, loc.address_line2, loc.city, loc.state, loc.postal_code, loc.country].filter(Boolean).join(', '),
              address_line1: loc.address_line1,
              address_line2: loc.address_line2,
              city: loc.city,
              state: loc.state,
              postal_code: loc.postal_code,
              country: loc.country,
              center_latitude: loc.latitude,
              center_longitude: loc.longitude,
              latitude: loc.latitude,
              longitude: loc.longitude,
              gps_radius_meters: loc.radius_meters,
              radius_meters: loc.radius_meters,
              is_primary: loc.is_primary,
              is_billing: loc.is_billing,
              is_shipping: loc.is_shipping,
              is_active: loc.is_active,
              is_company_location: loc.is_company_location,
              no_appointment_required: loc.no_appointment_required,
              notes: loc.notes,
              location_code: loc.location_code,
              cost_centre: loc.cost_centre,
              billing_entity_name: loc.billing_entity_name,
              billing_registration_number: loc.billing_registration_number,
              billing_tax_number: loc.billing_tax_number,
              billing_email: loc.billing_email,
              billing_phone: loc.billing_phone,
              billing_attention: loc.billing_attention,
            }),
          });
          if (locRes.ok) {
            createdLocationsCount++;
            const locData = await locRes.json().catch(() => null);
            const locId = locData?.data?.id ?? locData?.id;
            if (locId) locationIdByKey.set(loc._key, locId);
          }
        } catch {
          // continue creating remaining locations
        }
      }

      // 3. Create people (parallel), then link residents to their stand/unit
      const validPeople = people.filter((p) => p.first_name && p.last_name);
      const personResults = await Promise.allSettled(
        validPeople.map((p) => {
          const isResident = p.person_type === 'resident';
          const residentLocationId =
            isResident && p.resident_location_key
              ? locationIdByKey.get(p.resident_location_key)
              : undefined;
          return fetch(`/api/v1/contacts/${contactId}/people/`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              contact_id: contactId,
              first_name: p.first_name,
              last_name: p.last_name,
              email: p.email || '',
              phone: p.phone || '',
              mobile: p.mobile || '',
              direct_line: p.direct_line || '',
              job_title: p.job_title || '',
              department: p.department || '',
              is_primary: p.is_primary,
              is_billing_contact: p.is_billing_contact,
              is_technical_contact: p.is_technical_contact,
              is_decision_maker: p.is_decision_maker,
              preferred_contact_method: p.preferred_contact_method,
              is_active: p.is_active,
              notes: buildPersonNotes(p) || '',
              photo_url: p.photo_url || undefined,
              ...(isResident && {
                accepts_gate_calls: p.accepts_gate_calls,
                has_portal_access: p.has_portal_access,
                portal_role: p.has_portal_access ? 'user' : undefined,
                default_location_id: residentLocationId,
              }),
            }),
          }).then((r) => r.json());
        })
      );

      const createdPeopleCount = personResults.filter(
        (r) => r.status === 'fulfilled' && r.value?.success !== false
      ).length;

      // 4. Link residents to their stand/unit via contact_people_locations
      await Promise.allSettled(
        personResults.map(async (result, i) => {
          if (result.status !== 'fulfilled') return;
          const p = validPeople[i];
          if (p.person_type !== 'resident' || !p.resident_location_key) return;
          const locationId = locationIdByKey.get(p.resident_location_key);
          const personId = result.value?.data?.id ?? result.value?.id;
          if (!locationId || !personId) return;
          await fetch(`/api/v1/contacts/people/${personId}/locations`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              locations: [{
                location_id: locationId,
                is_primary: true,
                role_at_location: p.resident_role || 'resident',
              }],
            }),
          });
        })
      );

      notify.success(
        `Stakeholder created with ${createdPeopleCount} ${createdPeopleCount === 1 ? 'person' : 'people'} and ${createdLocationsCount} ${createdLocationsCount === 1 ? 'location' : 'locations'}`
      );
      clearOnboardingDraft();
      onSaved(contactId);
      onClose();
    } catch (error) {
      console.error('Stakeholder onboarding error:', error);
      notify.error('Failed to create stakeholder');
    } finally {
      setSaving(false);
    }
  };

  // ---- Update existing contact (edit mode) ----
  const handleUpdate = async () => {
    if (!editContactId) return;

    for (const step of steps) {
      if (step.key === 'review') continue;
      const r = validateStep(step.key);
      if (!r.ok) {
        setCurrentStep(step.key);
        notify.error(r.firstError);
        return;
      }
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const specialInstructions = buildProcurementInstructions(stakeholder);

      const updatePayload = {
        contact_type: stakeholder.contact_type,
        company_name: stakeholder.company_name || undefined,
        contact_person: stakeholder.contact_person || undefined,
        email: stakeholder.email || undefined,
        phone: stakeholder.phone || undefined,
        mobile: stakeholder.mobile || undefined,
        website: stakeholder.website || undefined,
        tax_number: stakeholder.tax_number || undefined,
        registration_number: stakeholder.registration_number || undefined,
        payment_terms_days: getPaymentTermsDays(stakeholder.payment_terms, stakeholder.payment_terms_other),
        credit_limit: stakeholder.credit_limit,
        currency: stakeholder.currency || 'ZAR',
        notes: stakeholder.notes || undefined,
        special_instructions: specialInstructions || undefined,
        is_active: stakeholder.is_active,
        account_status: stakeholder.account_status,
        logo_url: stakeholder.logo_url,
        photo_url: stakeholder.photo_url,
        legal_name: stakeholder.legal_name || undefined,
        legal_address: stakeholder.legal_address || undefined,
        trading_name: stakeholder.trading_name || undefined,
        industry: stakeholder.industry || undefined,
        company_size: stakeholder.company_size || undefined,
        annual_revenue: stakeholder.annual_revenue || undefined,
        bank_name: stakeholder.bank_name || undefined,
        bank_account_number: stakeholder.bank_account_number || undefined,
        bank_branch_code: stakeholder.bank_branch_code || undefined,
        bank_account_type: stakeholder.bank_account_type || undefined,
        bee_level: stakeholder.bee_level || undefined,
        bee_expiry_date: stakeholder.bee_expiry_date || undefined,
        insurance_provider: stakeholder.insurance_provider || undefined,
        insurance_policy_number: stakeholder.insurance_policy_number || undefined,
        insurance_expiry_date: stakeholder.insurance_expiry_date || undefined,
        license_number: stakeholder.license_number || undefined,
        license_expiry_date: stakeholder.license_expiry_date || undefined,
        trade_references: stakeholder.trade_references.filter((reference) =>
          [reference.company, reference.contact, reference.phone, reference.email].some(Boolean)
        ),
        certifications: stakeholder.certifications.filter((certification) =>
          [certification.name, certification.number, certification.expiry].some(Boolean)
        ),
        skills: stakeholder.skills,
        hourly_rate: stakeholder.hourly_rate,
        daily_rate: stakeholder.daily_rate,
        lead_time_days: stakeholder.lead_time_days,
        minimum_order_value: stakeholder.minimum_order_value,
        discount_percentage: stakeholder.discount_percentage,
        preferred_payment_method: stakeholder.preferred_payment_method || undefined,
        account_manager_id: stakeholder.account_manager_id ?? undefined,
      };

      const res = await fetch(`/api/v1/contacts/${editContactId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatePayload),
      });

      if (!res.ok) {
        const err = await res.json();
        notify.error(err.error || 'Failed to update contact');
        return;
      }

      notify.success('Contact updated successfully');
      clearOnboardingDraft();
      onSaved(editContactId ? Number(editContactId) : undefined);
      onClose();
    } catch (error) {
      console.error('Error updating contact:', error);
      notify.error('Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  // ---- Quick action: open finance report viewer for this contact ----
  const handleQuickAction = async (actionKey: string, contactId: number) => {
    setGeneratingReport(actionKey);
    try {
      const now = new Date();
      const dateFrom = new Date(now.getFullYear(), now.getMonth() - 12, 1).toISOString().split('T')[0];
      const dateTo = now.toISOString().split('T')[0];

      const reportConfig = QUICK_ACTION_REPORT_CONFIG[actionKey];
      const report = reportConfig ? getFinanceReportById(reportConfig.reportId) : undefined;

      if (!reportConfig || !report) {
        notify.error('This finance report is not configured yet');
        return;
      }

      setActiveFinanceReportView({
        report,
        initialParameters: reportConfig.getParameters(contactId, dateFrom, dateTo),
        autoExecute: reportConfig.autoExecute,
      });
    } catch (error) {
      console.error('Quick action error:', error);
      notify.error('Failed to open report');
    } finally {
      setGeneratingReport(null);
    }
  };

  // ============================================================================
  // RENDER — STEP BAR
  // ============================================================================

  const renderStepBar = () => (
    <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 overflow-x-auto" data-tour-id="stakeholder-onboarding-step-bar">
      {steps.map((step, idx) => {
        const isCompleted = idx < currentStepIndex;
        const isCurrent = idx === currentStepIndex;
        const StepIcon = step.icon;

        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div className={`flex-shrink-0 w-8 h-px ${isCompleted ? 'bg-green-400' : 'bg-gray-300 dark:bg-gray-600'}`} />
            )}
            <button
              onClick={() => goToStep(step.key)}
              disabled={idx > currentStepIndex}
              data-tour-id={`stakeholder-onboarding-step-${step.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                ${isCurrent ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600' : ''}
                ${isCompleted ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer' : ''}
                ${!isCurrent && !isCompleted ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : ''}
              `}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                  ${isCompleted ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' : ''}
                  ${isCurrent ? 'text-white' : ''}
                  ${!isCurrent && !isCompleted ? 'bg-gray-100 dark:bg-gray-700 text-gray-400' : ''}
                `}
                style={isCurrent ? { backgroundColor: headerColor } : undefined}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
              </div>
              <div className="hidden sm:block text-left">
                <div className="leading-tight">{step.label}</div>
                <div className="text-[10px] font-normal text-gray-400 dark:text-gray-500 leading-tight">{step.description}</div>
              </div>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ============================================================================
  // RENDER — STAKEHOLDER STEP
  // ============================================================================

  const renderStakeholderStep = () => (
    <div className="space-y-6" data-tour-id="stakeholder-onboarding-form">
      {/* Logo + Type */}
      <div className="flex items-start gap-6">
        {config.contact_show_logo && (
          <div className="flex-shrink-0">
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            <div
              onClick={() => !isReadOnly && logoInputRef.current?.click()}
              className={`w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden transition-colors group ${
                isReadOnly ? 'cursor-default' : 'hover:border-blue-500 cursor-pointer'
              }`}
            >
              {stakeholder.logo_url ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img src={stakeholder.logo_url} alt="Logo" className="w-full h-full object-contain" />
              ) : uploadingLogo ? (
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              ) : (
                <div className="text-center">
                  <Upload className="w-5 h-5 text-gray-400 group-hover:text-blue-500 mx-auto" />
                  <span className="text-[10px] text-gray-400 mt-1">Logo</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 grid grid-cols-2 gap-4">
          <SelectInput
            label={`${config.contacts_singular_label || 'Stakeholder'} Type`}
            value={stakeholder.contact_type}
            onChange={(v) => setStakeholder((prev) => ({ ...prev, contact_type: v }))}
            options={enabledContactTypes}
            required
          />
          <TextInput
            label="Company Name"
            value={stakeholder.company_name}
            onChange={(v) => setStakeholder((prev) => ({ ...prev, company_name: v }))}
            required={!stakeholder.contact_person}
            placeholder="e.g. Acme Corp"
          />
        </div>
      </div>

      {/* Contact Details */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <TextInput
            label="Email"
            value={stakeholder.email}
            onChange={(v) => setStakeholder((prev) => ({ ...prev, email: v }))}
            type="email"
            required={config.contact_email_required}
            placeholder="info@company.com"
          />
          {isCheckingEmail && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Checking email...</span>
            </div>
          )}
          {!isCheckingEmail && emailValidation.error && (
            <div className={`flex items-start gap-1.5 text-xs mt-1 ${emailValidation.isKnownUser ? 'text-amber-600' : 'text-red-600'}`}>
              {emailValidation.isKnownUser ? <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" /> : <X className="w-3 h-3 flex-shrink-0 mt-0.5" />}
              <span>{emailValidation.error}</span>
            </div>
          )}
          {!isCheckingEmail && emailValidation.isValid === true && !emailValidation.isKnownUser && stakeholder.email && (
            <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              <span>Email format is valid</span>
            </div>
          )}
        </div>
        <div>
          <PhoneInputWithCountryCode
            label={`Mobile${config.contact_mobile_required ? ' *' : ''}`}
            value={stakeholder.mobile || null}
            onChange={(v) => setStakeholder((prev) => ({ ...prev, mobile: v || '' }))}
            isDark={isDark}
          />
          {isCheckingMobile && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Checking phone...</span>
            </div>
          )}
          {!isCheckingMobile && mobileValidation.error && (
            <div className={`flex items-start gap-1.5 text-xs mt-1 ${mobileValidation.isKnownUser ? 'text-amber-600' : 'text-red-600'}`}>
              {mobileValidation.isKnownUser ? <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" /> : <X className="w-3 h-3 flex-shrink-0 mt-0.5" />}
              <span>{mobileValidation.error}</span>
            </div>
          )}
          {!isCheckingMobile && mobileValidation.isValid === true && !mobileValidation.isKnownUser && stakeholder.mobile && stakeholder.mobile.length > 5 && (
            <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              <span>Phone format is valid</span>
            </div>
          )}
        </div>
        {config.contact_phone_visible && (
          <div>
            <PhoneInputWithCountryCode
              label="Phone"
              value={stakeholder.phone || null}
              onChange={(v) => setStakeholder((prev) => ({ ...prev, phone: v || '' }))}
              isDark={isDark}
            />
          </div>
        )}
        {config.contact_website_visible && (
          <TextInput
            label="Website"
            value={stakeholder.website}
            onChange={(v) => setStakeholder((prev) => ({ ...prev, website: v }))}
            placeholder="https://..."
          />
        )}
      </div>

      <div className="space-y-4">
        <AccordionCard
          icon={Briefcase}
          title="Business Details"
          description="Corporate profile, legal identity and trading footprint."
          badge="Closed by default"
          isOpen={openStakeholderSections.has('business')}
          onToggle={() => setOpenStakeholderSections((prev) => { const next = new Set(prev); next.has('business') ? next.delete('business') : next.add('business'); return next; })}
        >
          <div className="grid grid-cols-2 gap-4 pt-3">
            <TextInput label="Legal Name" value={stakeholder.legal_name} onChange={(v) => setStakeholder((prev) => ({ ...prev, legal_name: v }))} />
            <TextInput label="Trading Name" value={stakeholder.trading_name} onChange={(v) => setStakeholder((prev) => ({ ...prev, trading_name: v }))} />
            <TextInput label="VAT / Tax Number" value={stakeholder.tax_number} onChange={(v) => setStakeholder((prev) => ({ ...prev, tax_number: v }))} />
            <TextInput
              label="Registration Number"
              value={stakeholder.registration_number}
              onChange={(v) => setStakeholder((prev) => ({ ...prev, registration_number: v }))}
            />
            <TextInput label="Industry" value={stakeholder.industry} onChange={(v) => setStakeholder((prev) => ({ ...prev, industry: v }))} />
            <SelectInput
              label="Company Size"
              value={stakeholder.company_size}
              onChange={(v) => setStakeholder((prev) => ({ ...prev, company_size: v }))}
              options={[
                { value: '1-10', label: '1–10 employees' },
                { value: '11-50', label: '11–50' },
                { value: '51-200', label: '51–200' },
                { value: '201-500', label: '201–500' },
                { value: '500+', label: '500+' },
              ]}
              placeholder="Select..."
            />
            <TextInput
              label="Annual Revenue"
              value={stakeholder.annual_revenue}
              onChange={(v) => setStakeholder((prev) => ({ ...prev, annual_revenue: v }))}
              placeholder="e.g. R50m - R100m"
            />
            <TextInput
              label="Primary Website / Procurement Portal"
              value={stakeholder.website}
              onChange={(v) => setStakeholder((prev) => ({ ...prev, website: v }))}
              placeholder="https://..."
            />
            <TextareaInput
              label="Legal / Registered Address"
              value={stakeholder.legal_address}
              onChange={(v) => setStakeholder((prev) => ({ ...prev, legal_address: v }))}
              rows={3}
              className="col-span-2"
            />
          </div>
        </AccordionCard>

        {showFinancialFields && (
          <AccordionCard
            icon={CreditCard}
            title="Financial"
            description="Commercial terms, payment profile, credit and banking details."
            badge="Closed by default"
            isOpen={openStakeholderSections.has('financial')}
            onToggle={() => setOpenStakeholderSections((prev) => { const next = new Set(prev); next.has('financial') ? next.delete('financial') : next.add('financial'); return next; })}
          >
            <div className="grid grid-cols-2 gap-4 pt-3">
              <SelectInput
                label="Payment Terms"
                value={stakeholder.payment_terms}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, payment_terms: v }))}
                options={[
                  { value: 'cod', label: 'COD' },
                  { value: 'fob', label: 'FOB' },
                  { value: '7_days', label: '7 Days' },
                  { value: '14_days', label: '14 Days' },
                  { value: '30_days', label: '30 Days' },
                  { value: '60_days', label: '60 Days' },
                  { value: '90_days', label: '90 Days' },
                  { value: 'other', label: 'Other' },
                ]}
              />
              {stakeholder.payment_terms === 'other' ? (
                <TextInput
                  label="Custom Terms"
                  value={stakeholder.payment_terms_other}
                  onChange={(v) => setStakeholder((prev) => ({ ...prev, payment_terms_other: v }))}
                  placeholder="e.g. 45 days from statement"
                />
              ) : (
                <TextInput
                  label="Preferred Payment Method"
                  value={stakeholder.preferred_payment_method}
                  onChange={(v) => setStakeholder((prev) => ({ ...prev, preferred_payment_method: v }))}
                  placeholder="EFT, debit order, card, etc."
                />
              )}
              <TextInput
                label={config.contact_require_credit_limit ? 'Credit Limit (R) *' : 'Credit Limit (R)'}
                value={stakeholder.credit_limit}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, credit_limit: v ? parseFloat(v) : null }))}
                type="number"
              />
              <SelectInput
                label="Account Status"
                value={stakeholder.account_status}
                onChange={(v) => handleAccountStatusChange(v as ContactAccountStatus, setStakeholder)}
                options={CONTACT_ACCOUNT_STATUS_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                disabled={isCreateMode}
              />
              <TextInput
                label="Lead Time (days)"
                value={stakeholder.lead_time_days}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, lead_time_days: v ? parseInt(v, 10) : null }))}
                type="number"
              />
              <TextInput
                label="Minimum Order Value (R)"
                value={stakeholder.minimum_order_value}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, minimum_order_value: v ? parseFloat(v) : null }))}
                type="number"
              />
              <TextInput
                label="Discount Percentage"
                value={stakeholder.discount_percentage}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, discount_percentage: v ? parseFloat(v) : null }))}
                type="number"
              />
              <TextInput label="Bank Name" value={stakeholder.bank_name} onChange={(v) => setStakeholder((prev) => ({ ...prev, bank_name: v }))} />
              <TextInput
                label="Account Number"
                value={stakeholder.bank_account_number}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, bank_account_number: v }))}
              />
              <TextInput
                label="Branch Code"
                value={stakeholder.bank_branch_code}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, bank_branch_code: v }))}
              />
              <SelectInput
                label="Account Type"
                value={stakeholder.bank_account_type}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, bank_account_type: v }))}
                options={[
                  { value: 'cheque', label: 'Cheque' },
                  { value: 'savings', label: 'Savings' },
                  { value: 'current', label: 'Current' },
                  { value: 'transmission', label: 'Transmission' },
                ]}
                placeholder="Select..."
              />
              {showContractorFields && (
                <>
                  <TextInput
                    label="Hourly Rate (R)"
                    value={stakeholder.hourly_rate}
                    onChange={(v) => setStakeholder((prev) => ({ ...prev, hourly_rate: v ? parseFloat(v) : null }))}
                    type="number"
                  />
                  <TextInput
                    label="Daily Rate (R)"
                    value={stakeholder.daily_rate}
                    onChange={(v) => setStakeholder((prev) => ({ ...prev, daily_rate: v ? parseFloat(v) : null }))}
                    type="number"
                  />
                </>
              )}
              <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200">
                <span className="font-semibold">Selected account state:</span>{' '}
                {isCreateMode
                  ? 'New stakeholder accounts start as Draft — visible in your lists while onboarding completes. Activate when ready to trade.'
                  : (CONTACT_ACCOUNT_STATUS_OPTIONS.find((option) => option.value === stakeholder.account_status)?.description || 'Normal trading account.')}
              </div>
            </div>
          </AccordionCard>
        )}

        <AccordionCard
          icon={Shield}
          title="Procurement & Compliance"
          description="Capture procurement-ready compliance, insurance, references and risk review details."
          badge={`${stakeholder.trade_references.length} ref${stakeholder.trade_references.length === 1 ? '' : 's'}`}
          isOpen={openStakeholderSections.has('procurement')}
          onToggle={() => setOpenStakeholderSections((prev) => { const next = new Set(prev); next.has('procurement') ? next.delete('procurement') : next.add('procurement'); return next; })}
        >
          <div className="space-y-5 pt-3">
            <div className="grid grid-cols-2 gap-4">
              <TextInput label="BEE Level" value={stakeholder.bee_level} onChange={(v) => setStakeholder((prev) => ({ ...prev, bee_level: v }))} />
              <TextInput
                label="BEE Expiry / Review"
                value={stakeholder.bee_expiry_date}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, bee_expiry_date: v }))}
                type="date"
              />
              <TextInput
                label="Public Liability Insurer"
                value={stakeholder.insurance_provider}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, insurance_provider: v }))}
                placeholder="Insurer or broker"
              />
              <TextInput
                label="Public Liability Policy Number"
                value={stakeholder.insurance_policy_number}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, insurance_policy_number: v }))}
              />
              <TextInput
                label="Insurance Expiry / Review"
                value={stakeholder.insurance_expiry_date}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, insurance_expiry_date: v }))}
                type="date"
              />
              <TextInput
                label="COIDA / LOGS Reference"
                value={stakeholder.license_number}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, license_number: v }))}
                placeholder="Letter of good standing or compliance reference"
              />
              <TextInput
                label="COIDA / LOGS Expiry / Review"
                value={stakeholder.license_expiry_date}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, license_expiry_date: v }))}
                type="date"
              />
              <TextInput
                label="Tax Clearance Verified At"
                value={stakeholder.tax_clearance_verified_at}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, tax_clearance_verified_at: v }))}
                type="date"
              />
              <TextInput
                label="Procurement Review Date"
                value={stakeholder.procurement_review_date}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, procurement_review_date: v }))}
                type="date"
              />
              <EmployeeLookup
                label="Account Manager / Relationship Owner"
                employees={accountManagerOptions.map((o) => ({ id: o.id, full_name: o.label, job_title: o.subLabel }))}
                value={stakeholder.account_manager_id}
                onChange={(id) => {
                  const selected = id != null ? accountManagerOptions.find((o) => o.id === id) : null;
                  setStakeholder((prev) => ({
                    ...prev,
                    account_manager_id: id,
                    account_manager_name: selected ? selected.label : '',
                  }));
                }}
                placeholder={accountManagerOptions.length === 0 ? 'No active employees found' : 'Search account managers...'}
                disabled={accountManagerOptions.length === 0}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextareaInput
                label="TransUnion / Credit Guarantee / Risk Notes"
                value={stakeholder.credit_assessment_notes}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, credit_assessment_notes: v }))}
                rows={4}
                placeholder="Credit bureau outcome, guarantee requirements, insurance caveats, etc."
              />
              <TextareaInput
                label="Procurement Review Notes"
                value={stakeholder.procurement_review_notes}
                onChange={(v) => setStakeholder((prev) => ({ ...prev, procurement_review_notes: v }))}
                rows={4}
                placeholder="Approved / pending items, missing documents, review commentary..."
              />
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Company References</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Useful for procurement packs, tender submissions and supplier due diligence.</p>
                </div>
                {!isReadOnly && (
                <button
                  type="button"
                  onClick={addTradeReference}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Reference
                </button>
                )}
              </div>

              {stakeholder.trade_references.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 py-3">
                  No company references added yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {stakeholder.trade_references.map((reference, idx) => (
                    <div key={`${reference.company || 'reference'}-${idx}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Reference {idx + 1}</div>
                        {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeTradeReference(idx)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <TextInput label="Company" value={reference.company} onChange={(v) => updateTradeReference(idx, { company: v })} />
                        <TextInput label="Contact Person" value={reference.contact} onChange={(v) => updateTradeReference(idx, { contact: v })} />
                        <TextInput label="Phone" value={reference.phone} onChange={(v) => updateTradeReference(idx, { phone: v })} />
                        <TextInput label="Email" value={reference.email} onChange={(v) => updateTradeReference(idx, { email: v })} type="email" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Certifications & Accreditations</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Capture ISO, OEM, safety and industry-specific credentials.</p>
                </div>
                {!isReadOnly && (
                <button
                  type="button"
                  onClick={addCertification}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Certification
                </button>
                )}
              </div>

              {stakeholder.certifications.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 py-3">
                  No certifications added yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {stakeholder.certifications.map((certification, idx) => (
                    <div key={`${certification.name || 'certification'}-${idx}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                          <Award className="w-4 h-4 text-amber-500" />
                          Certification {idx + 1}
                        </div>
                        {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeCertification(idx)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <TextInput label="Name" value={certification.name} onChange={(v) => updateCertification(idx, { name: v })} />
                        <TextInput label="Reference Number" value={certification.number} onChange={(v) => updateCertification(idx, { number: v })} />
                        <TextInput label="Expiry / Review" value={certification.expiry} onChange={(v) => updateCertification(idx, { expiry: v })} type="date" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </AccordionCard>
      </div>

      {/* Notes */}
      <TextareaInput
        label="Notes"
        value={stakeholder.notes}
        onChange={(v) => setStakeholder((prev) => ({ ...prev, notes: v }))}
        rows={3}
        placeholder="Any additional notes about this stakeholder..."
      />
    </div>
  );

  // ============================================================================
  // RENDER — PEOPLE STEP
  // ============================================================================

  const renderPeopleStep = () => (
    <div className="space-y-4" data-tour-id="stakeholder-onboarding-people">
      <div className="flex items-center justify-between">
        <SectionHeader icon={Users} title={config.contact_people_label || 'Contact People'} count={people.length} />
        {!isReadOnly && (
          <button
            onClick={addPerson}
            data-tour-id="stakeholder-onboarding-add-person"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Person
          </button>
        )}
      </div>

      {/* Person cards */}
      <div className="space-y-3">
        {people.map((person, idx) => {
          const isExpanded = activePersonIdx === idx;
          const hasError = Object.keys(validationErrors).some((k) => k.startsWith(`person_${idx}`));

          return (
            <div
              key={person._key}
              className={`border rounded-lg overflow-hidden transition-all ${
                hasError ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
              } ${isExpanded ? 'ring-1 ring-blue-300 dark:ring-blue-600' : ''}`}
            >
              {/* Card header */}
              <button
                type="button"
                onClick={() => setActivePersonIdx(isExpanded ? -1 : idx)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center text-sm font-semibold">
                  {person.first_name ? person.first_name[0].toUpperCase() : (idx + 1)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {person.first_name && person.last_name
                      ? `${person.first_name} ${person.last_name}`
                      : `Person ${idx + 1}`}
                  </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {[getPersonTypeLabel(person.person_type), person.job_title].filter(Boolean).join(' • ')}
                    </div>
                </div>
                {person.is_primary && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                    Primary
                  </span>
                )}
                {hasError && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>

              {/* Expanded form */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700/50 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <TextInput
                      label="First Name"
                      value={person.first_name}
                      onChange={(v) => updatePerson(idx, { first_name: v })}
                      required
                    />
                    <TextInput
                      label="Last Name"
                      value={person.last_name}
                      onChange={(v) => updatePerson(idx, { last_name: v })}
                      required
                    />
                    <SelectInput
                      label="Contact Role"
                      value={person.person_type}
                      onChange={(v) => updatePerson(idx, { person_type: v as PersonType })}
                      options={personTypeOptions}
                    />
                    <TextInput label="Job Title" value={person.job_title} onChange={(v) => updatePerson(idx, { job_title: v })} placeholder="e.g. Building Manager" />
                    <TextInput label="Department" value={person.department} onChange={(v) => updatePerson(idx, { department: v })} placeholder="e.g. Facilities" />
                    <SelectInput
                      label="Preferred Contact Method"
                      value={person.preferred_contact_method}
                      onChange={(v) => updatePerson(idx, { preferred_contact_method: v as PersonForm['preferred_contact_method'] })}
                      options={[
                        { value: 'email', label: 'Email' },
                        { value: 'phone', label: 'Phone' },
                        { value: 'mobile', label: 'Mobile' },
                        { value: 'whatsapp', label: 'WhatsApp' },
                      ]}
                    />
                    <div>
                      <TextInput
                        label="Email"
                        value={person.email}
                        onChange={(v) => updatePerson(idx, { email: v })}
                        type="email"
                        required={config.person_email_required}
                      />
                      {peopleChecking[idx]?.email && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Checking email...</span>
                        </div>
                      )}
                      {!peopleChecking[idx]?.email && peopleValidation[idx]?.email.error && (
                        <div className={`flex items-start gap-1.5 text-xs mt-1 ${peopleValidation[idx]?.email.isKnownUser ? 'text-amber-600' : 'text-red-600'}`}>
                          {peopleValidation[idx]?.email.isKnownUser ? <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" /> : <X className="w-3 h-3 flex-shrink-0 mt-0.5" />}
                          <span>{peopleValidation[idx]?.email.error}</span>
                        </div>
                      )}
                      {!peopleChecking[idx]?.email && peopleValidation[idx]?.email.isValid === true && !peopleValidation[idx]?.email.isKnownUser && person.email && (
                        <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
                          <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                          <span>Email format is valid</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <PhoneInputWithCountryCode
                        label={`Mobile${config.person_mobile_required ? ' *' : ''}`}
                        value={person.mobile || null}
                        onChange={(v) => updatePerson(idx, { mobile: v || '' })}
                        isDark={isDark}
                        showWhatsAppIcon={config.person_show_wa_link}
                      />
                      {peopleChecking[idx]?.mobile && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Checking phone...</span>
                        </div>
                      )}
                      {!peopleChecking[idx]?.mobile && peopleValidation[idx]?.mobile.error && (
                        <div className={`flex items-start gap-1.5 text-xs mt-1 ${peopleValidation[idx]?.mobile.isKnownUser ? 'text-amber-600' : 'text-red-600'}`}>
                          {peopleValidation[idx]?.mobile.isKnownUser ? <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" /> : <X className="w-3 h-3 flex-shrink-0 mt-0.5" />}
                          <span>{peopleValidation[idx]?.mobile.error}</span>
                        </div>
                      )}
                      {!peopleChecking[idx]?.mobile && peopleValidation[idx]?.mobile.isValid === true && !peopleValidation[idx]?.mobile.isKnownUser && person.mobile && person.mobile.length > 5 && (
                        <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
                          <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                          <span>Phone format is valid</span>
                        </div>
                      )}
                    </div>
                    {config.person_phone_visible && (
                      <div>
                        <PhoneInputWithCountryCode
                          label="Phone"
                          value={person.phone || null}
                          onChange={(v) => updatePerson(idx, { phone: v || '' })}
                          isDark={isDark}
                        />
                      </div>
                    )}
                    <TextInput
                      label="Direct Line"
                      value={person.direct_line}
                      onChange={(v) => updatePerson(idx, { direct_line: v })}
                      placeholder="Optional desk / extension"
                    />
                  </div>

                  {/* Resident residency block — stand link, gate calls, portal */}
                  {person.person_type === 'resident' && (
                    <div className={`rounded-lg border p-3 space-y-3 ${isDark ? 'border-teal-800/50 bg-teal-900/10' : 'border-teal-200 bg-teal-50/50'}`}>
                      <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-teal-300' : 'text-teal-700'}`}>
                        Residency
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <SelectInput
                          label="Stand / Unit"
                          value={person.resident_location_key}
                          onChange={(v) => updatePerson(idx, { resident_location_key: v })}
                          options={[
                            { value: '', label: '— Not linked —' },
                            ...locations
                              .filter((l) => l.location_name.trim())
                              .map((l) => ({ value: l._key, label: l.location_name })),
                          ]}
                        />
                        <SelectInput
                          label="Resident Role"
                          value={person.resident_role}
                          onChange={(v) => updatePerson(idx, { resident_role: v })}
                          options={[{ value: '', label: '— Select —' }, ...RESIDENT_ROLE_OPTIONS]}
                        />
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={person.accepts_gate_calls}
                            onChange={(e) => updatePerson(idx, { accepts_gate_calls: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-teal-600"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Accepts gate calls</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={person.has_portal_access}
                            onChange={(e) => updatePerson(idx, { has_portal_access: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-teal-600"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Resident portal access</span>
                        </label>
                      </div>
                      {locations.filter((l) => l.location_name.trim()).length === 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Add the stand/unit in the next step — the link is applied on save.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-4">
                    {([
                      ['is_decision_maker', 'Decision Maker'],
                      ['is_billing_contact', 'Billing Contact'],
                      ['is_technical_contact', 'Technical Contact'],
                    ] as const).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!person[key]}
                          onChange={(e) => updatePerson(idx, { [key]: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Notes */}
                  <TextareaInput
                    label="Notes"
                    value={person.notes}
                    onChange={(v) => updatePerson(idx, { notes: v })}
                    rows={2}
                  />

                  {/* Actions */}
                  {!isReadOnly && (
                  <div className="flex items-center gap-3 pt-2">
                    {!person.is_primary && (
                      <button
                        onClick={() => setPrimaryPerson(idx)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Set as primary
                      </button>
                    )}
                    <div className="flex-1" />
                    {people.length > 1 && (
                      <button
                        onClick={() => removePerson(idx)}
                        className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {people.length === 0 && (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No people added yet</p>
          <button onClick={addPerson} className="text-sm text-blue-600 hover:underline mt-1">
            Add the first contact person
          </button>
        </div>
      )}
    </div>
  );

  // ============================================================================
  // RENDER — LOCATIONS STEP
  // ============================================================================

  const renderLocationsStep = () => (
    <div className="space-y-4" data-tour-id="stakeholder-onboarding-locations">
      <div className="flex items-center justify-between">
        <SectionHeader icon={MapPinned} title={config.locations_label || 'Locations'} count={locations.length} />
        <div className="flex items-center gap-2">
          {isFinanceContext && !isReadOnly && (
            <button
              onClick={skipLocationsStep}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Skip Step
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {!isReadOnly && (
            <button
              onClick={addLocation}
              data-tour-id="stakeholder-onboarding-add-location"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Location
            </button>
          )}
        </div>
      </div>

      {isFinanceContext && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            Locations are <span className="font-semibold">optional</span> for finance stakeholders. GPS coordinates are not required here — you can skip this step and add locations later when scheduling an operations job or appointment for this contact.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {locations.map((loc, idx) => {
          const isExpanded = activeLocationIdx === idx;
          const hasError = Object.keys(validationErrors).some((k) => k.startsWith(`loc_${idx}`));

          return (
            <div
              key={loc._key}
              className={`border rounded-lg overflow-hidden transition-all ${
                hasError ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
              } ${isExpanded ? 'ring-1 ring-blue-300 dark:ring-blue-600' : ''}`}
            >
              {/* Card header */}
              <button
                type="button"
                onClick={() => setActiveLocationIdx(isExpanded ? -1 : idx)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {loc.location_name || `Location ${idx + 1}`}
                  </div>
                  {loc.city && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {[loc.city, loc.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
                {loc.is_primary && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                    Primary
                  </span>
                )}
                {loc.latitude != null && loc.longitude != null && (
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                    GPS ✓
                  </span>
                )}
                {hasError && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>

              {/* Expanded form */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700/50 space-y-4">
                  {/* Name + Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <TextInput
                      label="Location Name"
                      value={loc.location_name}
                      onChange={(v) => updateLocation(idx, { location_name: v })}
                      required
                      placeholder="e.g. Head Office"
                    />
                    <SelectInput
                      label="Type"
                      value={loc.location_type}
                      onChange={(v) => updateLocation(idx, { location_type: v })}
                      options={locationTypes}
                      required
                      placeholder="Select type..."
                    />
                  </div>

                  {/* Map / Search / Coordinates */}
                  {isFinanceContext ? (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setFinanceMapExpandedIdx(financeMapExpandedIdx === idx ? null : idx)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {formatGpsCoordinates(loc.latitude, loc.longitude)
                            ? `GPS: ${formatGpsCoordinates(loc.latitude, loc.longitude)}`
                            : 'Pin location on map (optional)'}
                        </span>
                        {financeMapExpandedIdx === idx ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {financeMapExpandedIdx === idx && (
                        <div className="h-[280px] border-t border-gray-200 dark:border-gray-700">
                          <GoogleMapsPicker
                            initialLat={loc.latitude?.toString() || ''}
                            initialLng={loc.longitude?.toString() || ''}
                            onLocationSelect={(lat: string, lng: string, address?: string) => handleLocationSelect(idx, { lat, lng, address })}
                            showSearch={true}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                  <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <div className="h-[280px]">
                      <GoogleMapsPicker
                        initialLat={loc.latitude?.toString() || ''}
                        initialLng={loc.longitude?.toString() || ''}
                        onLocationSelect={(lat: string, lng: string, address?: string) => handleLocationSelect(idx, { lat, lng, address })}
                        showSearch={true}
                      />
                    </div>
                    {formatGpsCoordinates(loc.latitude, loc.longitude) && (
                      <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 text-xs text-green-700 dark:text-green-400 flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        GPS: {formatGpsCoordinates(loc.latitude, loc.longitude)}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Geocode button */}
                  {(allowFreeTextLocationAddress || isFinanceContext) && loc.address_line1 && loc.latitude == null && (
                    <button
                      onClick={() => geocodeLocation(idx)}
                      disabled={geocoding}
                      className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                    >
                      {geocoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                      Geocode from address
                    </button>
                  )}

                  {/* Address fields (below map — populated from map/coords or manual entry) */}
                  <div className="grid grid-cols-2 gap-4">
                    <TextInput
                      label="Address Line 1"
                      value={loc.address_line1}
                      onChange={(v) => updateLocation(idx, { address_line1: v })}
                      className="col-span-2"
                      readOnly={!allowFreeTextLocationAddress && !isFinanceContext}
                      placeholder={allowFreeTextLocationAddress || isFinanceContext ? undefined : 'Populated from map lookup'}
                    />
                    <TextInput
                      label="Address Line 2"
                      value={loc.address_line2}
                      onChange={(v) => updateLocation(idx, { address_line2: v })}
                      className="col-span-2"
                      readOnly={!allowFreeTextLocationAddress && !isFinanceContext}
                      placeholder={allowFreeTextLocationAddress || isFinanceContext ? undefined : 'Populated from map lookup'}
                    />
                    <TextInput
                      label="City"
                      value={loc.city}
                      onChange={(v) => updateLocation(idx, { city: v })}
                      readOnly={!allowFreeTextLocationAddress && !isFinanceContext}
                      placeholder={allowFreeTextLocationAddress || isFinanceContext ? undefined : 'Populated from map lookup'}
                    />
                    <TextInput
                      label="Province / State"
                      value={loc.state}
                      onChange={(v) => updateLocation(idx, { state: v })}
                      readOnly={!allowFreeTextLocationAddress && !isFinanceContext}
                      placeholder={allowFreeTextLocationAddress || isFinanceContext ? undefined : 'Populated from map lookup'}
                    />
                    <TextInput
                      label="Postal Code"
                      value={loc.postal_code}
                      onChange={(v) => updateLocation(idx, { postal_code: v })}
                      readOnly={!allowFreeTextLocationAddress && !isFinanceContext}
                      placeholder={allowFreeTextLocationAddress || isFinanceContext ? undefined : 'Populated from map lookup'}
                    />
                    <TextInput
                      label="Country"
                      value={loc.country}
                      onChange={(v) => updateLocation(idx, { country: v })}
                      readOnly={!allowFreeTextLocationAddress && !isFinanceContext}
                      placeholder={allowFreeTextLocationAddress || isFinanceContext ? undefined : 'Populated from map lookup'}
                    />
                  </div>

                  {/* Toggles — billing/shipping only */}
                  <div className="flex flex-wrap gap-4">
                    {([
                      ['is_billing', 'Billing Address'],
                      ['is_shipping', 'Shipping Address'],
                    ] as const).map(([key, label]) => (
                      config.location_show_billing ? (
                        <label key={key} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!loc[key]}
                            onChange={(e) => updateLocation(idx, { [key]: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                        </label>
                      ) : null
                    ))}
                  </div>

                  {/* Billing details */}
                  {config.location_show_billing && (loc.is_billing || loc.is_shipping) && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Billing Details</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <TextInput label="Entity Name" value={loc.billing_entity_name} onChange={(v) => updateLocation(idx, { billing_entity_name: v })} />
                        <TextInput label="Reg. Number" value={loc.billing_registration_number} onChange={(v) => updateLocation(idx, { billing_registration_number: v })} />
                        <TextInput label="Tax Number" value={loc.billing_tax_number} onChange={(v) => updateLocation(idx, { billing_tax_number: v })} />
                        <TextInput label="Billing Email" value={loc.billing_email} onChange={(v) => updateLocation(idx, { billing_email: v })} />
                        <TextInput label="Billing Phone" value={loc.billing_phone} onChange={(v) => updateLocation(idx, { billing_phone: v })} />
                        <TextInput label="Attention" value={loc.billing_attention} onChange={(v) => updateLocation(idx, { billing_attention: v })} />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {!isReadOnly && (
                  <div className="flex items-center gap-3 pt-2">
                    {!loc.is_primary && (
                      <button onClick={() => setPrimaryLocation(idx)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                        Set as primary
                      </button>
                    )}
                    <div className="flex-1" />
                    {locations.length > 1 && (
                      <button
                        onClick={() => removeLocation(idx)}
                        className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {locations.length === 0 && (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500">
          <MapPinned className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No locations added yet</p>
          <button onClick={addLocation} className="text-sm text-blue-600 hover:underline mt-1">
            Add the first location
          </button>
        </div>
      )}
    </div>
  );

  // ============================================================================
  // RENDER — REVIEW STEP
  // ============================================================================

  const renderReviewStep = () => {
    const typeLabel = getTypeLabel?.(stakeholder.contact_type) || stakeholder.contact_type;
    const procurementHighlights = [
      stakeholder.insurance_provider ? `Public liability: ${stakeholder.insurance_provider}` : null,
      stakeholder.tax_clearance_verified_at ? `Tax verified: ${stakeholder.tax_clearance_verified_at}` : null,
      stakeholder.procurement_review_date ? `Review: ${stakeholder.procurement_review_date}` : null,
      stakeholder.account_manager_name ? `Account manager: ${stakeholder.account_manager_name}` : null,
      stakeholder.trade_references.length > 0 ? `${stakeholder.trade_references.length} company reference${stakeholder.trade_references.length === 1 ? '' : 's'}` : null,
      stakeholder.certifications.length > 0 ? `${stakeholder.certifications.length} certification${stakeholder.certifications.length === 1 ? '' : 's'}` : null,
    ].filter(Boolean) as string[];

    return (
      <div className="space-y-5" data-tour-id="stakeholder-onboarding-review">
        {/* Stakeholder summary */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            {stakeholder.logo_url && (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img src={stakeholder.logo_url} alt="" className="w-10 h-10 rounded-lg object-contain border border-gray-200 dark:border-gray-600" />
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {stakeholder.company_name || stakeholder.contact_person || 'Unnamed'}
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{typeLabel}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {stakeholder.email && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Mail className="w-3.5 h-3.5" /> {stakeholder.email}
              </div>
            )}
            {stakeholder.mobile && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Phone className="w-3.5 h-3.5" /> {stakeholder.mobile}
              </div>
            )}
            {stakeholder.tax_number && (
              <div className="text-gray-600 dark:text-gray-400">VAT: {stakeholder.tax_number}</div>
            )}
            {stakeholder.registration_number && (
              <div className="text-gray-600 dark:text-gray-400">Reg: {stakeholder.registration_number}</div>
            )}
          </div>
        </div>

        {procurementHighlights.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-blue-500" />
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Procurement Snapshot</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {procurementHighlights.map((item) => (
                <span key={item} className="text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* People summary */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-purple-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {config.contact_people_label || 'People'} ({people.filter((p) => p.first_name).length})
            </h4>
          </div>
          <div className="space-y-2">
            {people.filter((p) => p.first_name).map((p) => (
              <div
                key={p._key}
                className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
              >
                <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center text-[10px] font-bold">
                  {p.first_name[0]}{p.last_name?.[0] || ''}
                </div>
                <span className="font-medium">{p.first_name} {p.last_name}</span>
                <span className="text-[10px] text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full">{getPersonTypeLabel(p.person_type)}</span>
                {p.job_title && <span className="text-gray-400">— {p.job_title}</span>}
                {p.is_primary && (
                  <span className="text-[10px] text-green-600 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">Primary</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Locations summary */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPinned className="w-4 h-4 text-emerald-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {config.locations_label || 'Locations'} ({locations.filter((l) => l.location_name).length})
            </h4>
          </div>
          <div className="space-y-2">
            {locations.filter((l) => l.location_name).map((l) => (
              <div
                key={l._key}
                className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
              >
                <MapPin className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="font-medium">{l.location_name}</span>
                {l.city && <span className="text-gray-400">— {l.city}</span>}
                {l.latitude && l.longitude && (
                  <span className="text-[10px] text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-full">GPS ✓</span>
                )}
                {l.is_primary && (
                  <span className="text-[10px] text-green-600 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">Primary</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Warnings */}
        {locations.some((l) => l.location_name && !l.latitude) && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Some locations are missing GPS coordinates. You can add them later from the location detail view.</span>
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // RENDER — MAIN
  // ============================================================================

  if (!isOpen) return null;

  const stepContent: Record<StakeholderStep, React.ReactNode> = {
    stakeholder: renderStakeholderStep(),
    people: renderPeopleStep(),
    locations: renderLocationsStep(),
    review: renderReviewStep(),
  };

  return (
    <>
    <div className="fixed inset-0 z-[130] overflow-hidden bg-black/50 backdrop-blur-sm" data-tour-id="stakeholder-onboarding">
      <div className="flex items-center justify-center h-full">
        <div className="relative w-full h-full bg-white dark:bg-gray-900 flex flex-col">
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ backgroundColor: headerColor }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                {portalAccessMode ? (
                  <Send className="w-5 h-5 text-white" />
                ) : (
                  <Building2 className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {portalAccessMode ? 'Grant Financial Customer Portal Access' : (isReadOnly ? 'View' : isEditMode ? 'Edit' : 'New') + ' ' + (config.contacts_singular_label || 'Stakeholder')}
                </h3>
                <p className="text-xs text-white/70">
                  {portalAccessMode ? `Send invitation to ${portalAccessData?.first_name} ${portalAccessData?.last_name}` : (isReadOnly ? 'Contact details (read-only)' : isEditMode ? 'Update company, people & locations' : 'Set up company, people & locations in one go')}
                </p>
              </div>
              <UserDocsLink docsPath="/total-operations/workflows/service-operations/contacts-stakeholders#adding-a-stakeholder" label="User guide: Contacts & stakeholders" className="text-white/70 hover:text-white" />
            </div>
            {!customerPortalEnabled && portalAccessMode && (
              <div className="ml-4 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/40 rounded-lg">
                <p className="text-xs text-yellow-200">Customer portal is disabled</p>
              </div>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Quick Actions Bar (edit/view mode) */}
          {!portalAccessMode && !isCreateMode && editContactId && (
            <div className="flex items-center gap-2 px-6 py-2.5 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1 flex-shrink-0">Account Actions:</span>
              {[
                { key: 'customer_statement', label: 'Customer Statement', icon: FileText },
              ].map((qa) => {
                const Icon = qa.icon;
                return (
                  <button
                    key={qa.key}
                    onClick={() => void handleQuickAction(qa.key, editContactId)}
                    disabled={generatingReport === qa.key}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    {generatingReport === qa.key ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Icon className="w-3.5 h-3.5" />
                    )}
                    {qa.label}
                  </button>
                );
              })}
              <span className="ml-2 hidden lg:inline text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Global receivables reports are available in the Finance Reports workspace.
              </span>
            </div>
          )}

          {/* Step bar */}
          {!portalAccessMode && !activeFinanceReportView && renderStepBar()}

          {/* Content */}
          <div
            className={`flex-1 overflow-y-auto px-6 py-5 ${isReadOnly ? '[&_input]:pointer-events-none [&_input]:opacity-75 [&_select]:pointer-events-none [&_select]:opacity-75 [&_textarea]:pointer-events-none [&_textarea]:opacity-75 [&_input]:bg-gray-100 [&_input]:dark:bg-gray-900/60 [&_select]:bg-gray-100 [&_select]:dark:bg-gray-900/60 [&_textarea]:bg-gray-100 [&_textarea]:dark:bg-gray-900/60' : ''}`}
            data-tour-id={`stakeholder-onboarding-content-${currentStep}`}
          >
            {activeFinanceReportView ? (
              <FinanceReportViewer
                report={activeFinanceReportView.report}
                initialParameters={activeFinanceReportView.initialParameters}
                autoExecute={activeFinanceReportView.autoExecute}
                onBack={() => setActiveFinanceReportView(null)}
              />
            ) : portalAccessMode ? (
              <div className="max-w-2xl mx-auto py-8">
                {!customerPortalEnabled ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Customer Portal Disabled</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      The customer portal is currently disabled. Please enable it in admin-hub settings under Portal Configuration.
                    </p>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : portalLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
                  </div>
                ) : portalInvitationSent ? (
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      Invitation Sent Successfully
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 max-w-md">
                      An invitation has been sent to {portalAccessData?.email || portalAccessData?.phone}. 
                      The user will need to set their password and verify their email or phone to create their account.
                    </p>
                    <button
                      onClick={onClose}
                      className="mt-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    {portalAccessData && (
                      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                          Contact Person Details
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Name:</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {portalAccessData.first_name} {portalAccessData.last_name}
                            </span>
                          </div>
                          {portalAccessData.email && (
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Email:</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {portalAccessData.email}
                              </span>
                            </div>
                          )}
                          {portalAccessData.phone && (
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {portalAccessData.phone}
                              </span>
                            </div>
                          )}
                          {portalAccessData.job_title && (
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Job Title:</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {portalAccessData.job_title}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {portalError && (
                      <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-800 dark:text-red-200">{portalError}</p>
                      </div>
                    )}

                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-6">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        About Portal Access
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        The invitation will be sent via email or WhatsApp. The user will receive a link to set their password and verify their contact information. 
                        Their account will only be created after they complete this verification step.
                      </p>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSendPortalInvitation}
                        disabled={portalLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {portalLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Invitation
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : loadingContact ? (
              <div className="flex items-center justify-center h-64">
                <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
                <span className="ml-3 text-gray-500">Loading contact details...</span>
              </div>
            ) : (
              stepContent[currentStep]
            )}
          </div>

          {/* Footer */}
          {!portalAccessMode && !activeFinanceReportView && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0" data-tour-id="stakeholder-onboarding-footer">
              <div>
                {!isFirstStep && !isReadOnly && (
                  <button
                    onClick={handleBack}
                    data-tour-id="stakeholder-onboarding-back"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {isReadOnly ? (
                  /* In view mode, no action buttons needed */
                  null
                ) : isLastStep ? (
                  <button
                    onClick={isEditMode ? handleUpdate : handleSubmit}
                    disabled={saving}
                    data-tour-id="stakeholder-onboarding-create"
                    className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                    style={{ backgroundColor: headerColor }}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {isEditMode ? 'Save Changes' : `Create ${config.contacts_singular_label || 'Stakeholder'}`}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    data-tour-id="stakeholder-onboarding-next"
                    className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: headerColor }}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>

      {!portalAccessMode && !isReadOnly && (
        <GuidedTour
          moduleKey="total-finance"
          activeTab={currentStep}
          onNavigate={(step) => {
            if (typeof step === 'string') {
              goToStep(step as StakeholderStep);
            }
          }}
          embeddedInModal
          autoFlowId="stakeholder-onboarding-wizard"
        />
      )}
    </>
  );
}
